/**
 * Luqta SDK UI Renderer
 *
 * Handles the complete pre-configured UI flow:
 * - Contest list display
 * - Participation flow
 * - Contest detail with levels
 * - Level completion by type
 * - Congratulation popups
 */

import { injectStyles, generateCSSVariables } from './styles';
import { ICONS, getLevelTypeIcon, getLevelTypeName } from './icons';
import {
  BrandingConfig,
  Contest,
  Level,
  LevelWithProgress,
  ParticipateProgress,
  LuqtaError,
  UIAction,
  QuizQuestion,
  QuizProgress,
  QuizAttempt,
  Quiz,
  USER_ENDPOINTS,
} from '../types';

// ============================================================================
// TYPES
// ============================================================================

interface RendererConfig {
  containerId: string;
  branding?: BrandingConfig;
  locale?: 'en' | 'ar';
  rtl?: boolean;
  showProfileIcon?: boolean;
  onAction?: (action: UIAction) => void;
  onError?: (error: LuqtaError) => void;
}

interface RendererState {
  currentView: 'contests' | 'contest-detail' | 'contest-info';
  contests: Contest[];
  selectedContest: Contest | null;
  currentContest: Contest | null;
  currentInfoPageData: any;
  loading: boolean;
  error: string | null;
  profileData: any | null;
  showProfileSheet: boolean;
}

type ApiClient = {
  getPublicContests: () => Promise<any>;
  fetchContests: (params?: { page?: number; per_page?: number }) => Promise<any>;
  isSdkReady: () => boolean;
  getContestDetailsProgress: (contestId: number) => Promise<any>;
  getContestDetails: (contestId: number, accessCode?: string) => Promise<any>;
  participateContest: (contestId: number, accessCode?: string) => Promise<any>;
  contests: {
    participate: (contestId: number, accessCode?: string) => Promise<any>;
    getProgress: (contestId: number) => Promise<any>;
    compete: (contestId: number) => Promise<any>;
  };
  levels: {
    complete: (levelId: number, data: any) => Promise<any>;
    completeWithImage: (levelId: number, imageBase64: string) => Promise<any>;
    getCongratulation: (levelId: number, contestId: number) => Promise<any>;
  };
  quiz: {
    start: (quizId: number, levelId: number) => Promise<any>;
    submitAnswer: (attemptId: number, questionId: number, optionId: number, levelId: number) => Promise<any>;
    submit: (attemptId: number) => Promise<any>;
  };
  isInitialized: () => boolean;
  initializeUser: () => Promise<void>;
  applicationRequest: (endpoint: string, options?: any) => Promise<any>;
  profile: {
    get: () => Promise<any>;
  };
};

// ============================================================================
// TRANSLATIONS
// ============================================================================

const TRANSLATIONS = {
  en: {
    contests: 'Contests',
    loading: 'Loading...',
    noContests: 'No contests available',
    noContestsDesc: 'No contests exist for this API key. Please create contests in the Luqta dashboard.',
    participate: 'Join Contest',
    participating: 'Joining...',
    viewProgress: 'View Progress',
    levels: 'Levels',
    completed: 'Completed',
    inProgress: 'In Progress',
    locked: 'Locked',
    points: 'points',
    totalPoints: 'Total Points',
    participants: 'Participants',
    daysLeft: 'Days Left',
    submitAnswer: 'Submit Answer',
    submitting: 'Submitting...',
    enterAnswer: 'Enter your answer',
    scanQR: 'Scan QR code',
    scanQRDescription: 'Please scan a code to verify that you\'ve complete the level.',
    scanNow: 'Scan Now',
    openCamera: 'Open Camera',
    scanningQR: 'Scanning...',
    cameraPermissionDenied: 'Camera permission denied',
    qrCodeDetected: 'QR Code detected!',
    visitLink: 'Visit Link',
    uploadPhoto: 'Upload photo',
    uploadPhotoDescription: 'Please upload the photo to verify that you\'ve complete the level.',
    uploadPhotoBtn: 'Upload photo',
    supportedFormats: 'Supported format JPEG, PNG.',
    maxPhotoSize: 'Max photo size 2 MB.',
    uploadImage: 'Upload Image',
    takeQuiz: 'Take Quiz',
    congratulations: 'Congratulations!',
    levelCompleted: 'You completed this level!',
    contestCompleted: 'You completed the contest!',
    earnedPoints: 'You earned',
    continue: 'Continue',
    close: 'Close',
    back: 'Back',
    joinToParticipate: 'Join to participate',
    participateTitle: 'Ready to join?',
    participateDesc: 'Join this contest to start completing levels and earn rewards!',
    cancel: 'Cancel',
    error: 'Something went wrong',
    retry: 'Retry',
    // Quiz translations
    quizTitle: 'Quiz',
    question: 'Question',
    of: 'of',
    nextQuestion: 'Next Question',
    submitQuiz: 'Submit Quiz',
    correctAnswer: 'Correct!',
    incorrectAnswer: 'Incorrect',
    quizInstruction: 'Answer correctly to earn points and unlock the next level.',
    quizAnswerBtn: 'Answer',
    quizCompleted: 'Quiz Completed!',
    yourScore: 'Your Score',
    passed: 'Passed',
    failed: 'Failed',
    timeRemaining: 'Time Remaining',
    selectAnswer: 'Select an answer',
    left: 'Left',
    join: 'Join',
    win: 'Win',
    // Detail page
    progress: 'Progress',
    seeMore: 'See More',
    aboutCompany: 'About company',
    play: 'Play',
    prizes: 'Prizes',
    completeAllLevels: 'Complete all contest Levels to earn Point and enter the draw',
    taskDescription: 'Task Description',
    // Participation popup
    participationRequired: 'Join This Contest',
    participationRequiredDesc: 'You need to join this contest before viewing progress.',
    enterAccessCode: 'Enter Access Code',
    accessCodePlaceholder: 'Access code for private contest',
    joining: 'Joining...',
    joinSuccess: 'Successfully joined!',
    privateContest: 'Private Contest',
    // Congratulation modal
    youEarned: 'You earned',
    youFinishedIn: 'You finished in',
    shareAndEarn: 'Share and earn',
    goToNextLevel: 'Go to the next level',
    backToContest: 'Back to contest',
    // Contest completion modal
    congratulationsContest: 'Congratulations! You completed this contest.',
    totalPointsEarned: 'Total Points Earned',
    levelsCompleted: 'Levels Completed',
    timeSpent: 'Time Spent',
    drawDate: 'Draw Date',
    viewPrizes: 'View Prizes',
    backToContests: 'Back to Contests',
    // Contest info modal
    contestInfo: 'Contest Info',
    loadingInfo: 'Loading contest info...',
    startDateLabel: 'Start Date',
    endDateLabel: 'End Date',
    prizeAnnounceDate: 'Prize Announcement',
    locationLabel: 'Location',
    genderLabel: 'Gender',
    totalPointsLabel: 'Total Points',
    numberOfWinners: 'Number of Winners',
    ageGroups: 'Age Groups',
    contestType: 'Type',
    publicContest: 'Public',
    drawRules: 'Draw Rules',
    mustCompleteProfile: 'Must Complete Profile',
    mustCompleteOneTask: 'Must Complete One Task',
    specificPointsRequired: 'Specific Points Required',
    yes: 'Yes',
    no: 'No',
    bothGenders: 'Both',
    male: 'Male',
    female: 'Female',
    all: 'All',
    accessCodeRequired: 'Access Code Required',
    enterAccessCodeInfo: 'Enter access code to view contest details',
    viewDetails: 'View Details',
    yourProgress: 'Your Progress',
    continueContest: 'Continue',
    userNotInitialized: 'Please sign in to participate',
    participationSuccess: 'You have successfully joined the contest!',
    levelNotStarted: 'This level has not started yet',
    levelEnded: 'This level has ended',
    levelUnavailableTitle: 'Level unavailable',
    contestUnavailableTitle: 'Contest unavailable',
    contestNotStarted: 'This contest has not started yet',
    contestEndedMessage: 'This contest has ended',
    ok: 'OK',
    // Profile sheet
    profileVerified: 'Verified',
    profileTotalPoints: 'Total Points',
    profileStatistics: 'Statistics',
    profileContestsJoined: 'Contests Joined',
    profileCompleted: 'Completed',
    profileInProgress: 'In Progress',
    profileLevelsCompleted: 'Levels Done',
  },
  ar: {
    contests: 'المسابقات',
    loading: 'جاري التحميل...',
    noContests: 'لا توجد مسابقات متاحة',
    noContestsDesc: 'لا توجد مسابقات لهذا المفتاح. يرجى إنشاء مسابقات في لوحة تحكم Luqta.',
    participate: 'انضم للمسابقة',
    participating: 'جاري الانضمام...',
    viewProgress: 'عرض التقدم',
    levels: 'المستويات',
    completed: 'مكتمل',
    inProgress: 'جاري',
    locked: 'مقفل',
    points: 'نقاط',
    totalPoints: 'إجمالي النقاط',
    participants: 'المشاركين',
    daysLeft: 'يوم متبقي',
    submitAnswer: 'إرسال الإجابة',
    submitting: 'جاري الإرسال...',
    enterAnswer: 'أدخل إجابتك',
    scanQR: 'مسح رمز QR',
    scanQRDescription: 'يرجى مسح الرمز للتحقق من إكمال المستوى.',
    scanNow: 'مسح الآن',
    openCamera: 'فتح الكاميرا',
    scanningQR: 'جاري المسح...',
    cameraPermissionDenied: 'تم رفض إذن الكاميرا',
    qrCodeDetected: 'تم اكتشاف رمز QR!',
    visitLink: 'زيارة الرابط',
    uploadPhoto: 'رفع صورة',
    uploadPhotoDescription: 'يرجى رفع الصورة للتحقق من إكمال المستوى.',
    uploadPhotoBtn: 'رفع صورة',
    supportedFormats: 'الصيغ المدعومة JPEG, PNG.',
    maxPhotoSize: 'الحجم الأقصى 2 ميجابايت.',
    uploadImage: 'رفع صورة',
    takeQuiz: 'بدء الاختبار',
    congratulations: 'تهانينا!',
    levelCompleted: 'لقد أكملت هذا المستوى!',
    contestCompleted: 'لقد أكملت المسابقة!',
    earnedPoints: 'لقد ربحت',
    continue: 'متابعة',
    close: 'إغلاق',
    back: 'رجوع',
    joinToParticipate: 'انضم للمشاركة',
    participateTitle: 'مستعد للانضمام؟',
    participateDesc: 'انضم لهذه المسابقة لبدء إكمال المستويات وكسب المكافآت!',
    cancel: 'إلغاء',
    error: 'حدث خطأ ما',
    retry: 'إعادة المحاولة',
    // Quiz translations
    quizTitle: 'اختبار',
    question: 'سؤال',
    of: 'من',
    nextQuestion: 'السؤال التالي',
    submitQuiz: 'إرسال الاختبار',
    correctAnswer: 'صحيح!',
    incorrectAnswer: 'غير صحيح',
    quizInstruction: 'أجب بشكل صحيح لكسب النقاط وفتح المستوى التالي.',
    quizAnswerBtn: 'إجابة',
    quizCompleted: 'اكتمل الاختبار!',
    yourScore: 'نتيجتك',
    passed: 'ناجح',
    failed: 'راسب',
    timeRemaining: 'الوقت المتبقي',
    selectAnswer: 'اختر إجابة',
    left: 'متبقي',
    join: 'انضم',
    win: 'اربح',
    // Detail page
    progress: 'التقدم',
    seeMore: 'المزيد',
    aboutCompany: 'عن الشركة',
    play: 'ابدأ',
    prizes: 'الجوائز',
    completeAllLevels: 'أكمل جميع مستويات المسابقة لكسب النقاط والدخول في السحب',
    taskDescription: 'وصف المهمة',
    // Participation popup
    participationRequired: 'انضم لهذه المسابقة',
    participationRequiredDesc: 'يجب عليك الانضمام لهذه المسابقة قبل عرض التقدم.',
    enterAccessCode: 'أدخل رمز الدخول',
    accessCodePlaceholder: 'رمز الدخول للمسابقة الخاصة',
    joining: 'جاري الانضمام...',
    joinSuccess: 'تم الانضمام بنجاح!',
    privateContest: 'مسابقة خاصة',
    // Congratulation modal
    youEarned: 'لقد ربحت',
    youFinishedIn: 'أنهيت في',
    shareAndEarn: 'شارك واربح',
    goToNextLevel: 'انتقل للمستوى التالي',
    backToContest: 'العودة للمسابقة',
    // Contest completion modal
    congratulationsContest: 'تهانينا! لقد أكملت هذه المسابقة.',
    totalPointsEarned: 'إجمالي النقاط المكتسبة',
    levelsCompleted: 'المستويات المكتملة',
    timeSpent: 'الوقت المستغرق',
    drawDate: 'تاريخ السحب',
    viewPrizes: 'عرض الجوائز',
    backToContests: 'العودة للمسابقات',
    // Contest info modal
    contestInfo: 'معلومات المسابقة',
    loadingInfo: 'جاري تحميل المعلومات...',
    startDateLabel: 'تاريخ البدء',
    endDateLabel: 'تاريخ الانتهاء',
    prizeAnnounceDate: 'تاريخ إعلان الجوائز',
    locationLabel: 'الموقع',
    genderLabel: 'الجنس',
    totalPointsLabel: 'إجمالي النقاط',
    numberOfWinners: 'عدد الفائزين',
    ageGroups: 'الفئات العمرية',
    contestType: 'النوع',
    publicContest: 'عامة',
    drawRules: 'قواعد السحب',
    mustCompleteProfile: 'يجب إكمال الملف الشخصي',
    mustCompleteOneTask: 'يجب إكمال مهمة واحدة',
    specificPointsRequired: 'نقاط محددة مطلوبة',
    yes: 'نعم',
    no: 'لا',
    bothGenders: 'كلاهما',
    male: 'ذكر',
    female: 'أنثى',
    all: 'الكل',
    accessCodeRequired: 'رمز الدخول مطلوب',
    enterAccessCodeInfo: 'أدخل رمز الدخول لعرض تفاصيل المسابقة',
    viewDetails: 'عرض التفاصيل',
    yourProgress: 'تقدمك',
    continueContest: 'استمر',
    userNotInitialized: 'يرجى تسجيل الدخول للمشاركة',
    participationSuccess: 'لقد انضممت بنجاح للمسابقة!',
    levelNotStarted: 'لم يبدأ هذا المستوى بعد',
    levelEnded: 'انتهى هذا المستوى',
    levelUnavailableTitle: 'المستوى غير متاح',
    contestUnavailableTitle: 'المسابقة غير متاحة',
    contestNotStarted: 'لم تبدأ هذه المسابقة بعد',
    contestEndedMessage: 'انتهت هذه المسابقة',
    ok: 'موافق',
    // Profile sheet
    profileVerified: 'موثق',
    profileTotalPoints: 'إجمالي النقاط',
    profileStatistics: 'الإحصائيات',
    profileContestsJoined: 'مسابقات انضممت',
    profileCompleted: 'مكتملة',
    profileInProgress: 'جارية',
    profileLevelsCompleted: 'مستويات منجزة',
  }
};

// ============================================================================
// LUQTA UI RENDERER CLASS
// ============================================================================

export class LuqtaUIRenderer {
  private container: HTMLElement;
  private config: RendererConfig;
  private client: ApiClient;
  private state: RendererState;
  private t: typeof TRANSLATIONS['en'];
  private static readonly ACCESS_CODE_CACHE_KEY = 'luqta_access_codes';

  constructor(client: ApiClient, config: RendererConfig) {
    const container = document.getElementById(config.containerId);
    if (!container) {
      throw new LuqtaError(
        `Container element "${config.containerId}" not found`,
        'CONTAINER_NOT_FOUND'
      );
    }

    this.container = container;
    this.config = config;
    this.client = client;
    this.t = TRANSLATIONS[config.locale || 'en'];

    this.state = {
      currentView: 'contests',
      contests: [],
      selectedContest: null,
      currentContest: null,
      currentInfoPageData: null,
      loading: false,
      error: null,
      profileData: null,
      showProfileSheet: false,
    };
  }

  // ============================================================================
  // ACCESS CODE CACHE (localStorage)
  // ============================================================================

  private getAccessCodeFromCache(contestId: number): string | undefined {
    try {
      const cache = localStorage.getItem(LuqtaUIRenderer.ACCESS_CODE_CACHE_KEY);
      if (cache) {
        const codes = JSON.parse(cache);
        return codes[contestId];
      }
    } catch (e) {
      // localStorage not available or parse error
    }
    return undefined;
  }

  private setAccessCodeToCache(contestId: number, accessCode: string): void {
    try {
      const cache = localStorage.getItem(LuqtaUIRenderer.ACCESS_CODE_CACHE_KEY);
      const codes = cache ? JSON.parse(cache) : {};
      codes[contestId] = accessCode;
      localStorage.setItem(LuqtaUIRenderer.ACCESS_CODE_CACHE_KEY, JSON.stringify(codes));
    } catch (e) {
      // localStorage not available
    }
  }

  private removeAccessCodeFromCache(contestId: number): void {
    try {
      const cache = localStorage.getItem(LuqtaUIRenderer.ACCESS_CODE_CACHE_KEY);
      if (cache) {
        const codes = JSON.parse(cache);
        delete codes[contestId];
        localStorage.setItem(LuqtaUIRenderer.ACCESS_CODE_CACHE_KEY, JSON.stringify(codes));
      }
    } catch (e) {
      // localStorage not available
    }
  }

  // ============================================================================
  // PUBLIC METHODS
  // ============================================================================

  /**
   * Initialize and render the UI
   */
  async render(): Promise<void> {
    // Inject styles
    injectStyles(this.config.branding);

    // Apply CSS variables to container
    this.container.style.cssText = generateCSSVariables(this.config.branding || {});
    this.container.classList.add('luqta-sdk');

    if (this.config.rtl) {
      this.container.setAttribute('dir', 'rtl');
    }

    // Show loading
    this.showLoading();

    // Load contests
    await this.loadContests();
  }

  /**
   * Render the UI with pre-loaded contests
   * Used when contests are already fetched during SDK initialization
   */
  async renderWithContests(contests: Contest[]): Promise<void> {
    // Inject styles
    injectStyles(this.config.branding);

    // Apply CSS variables to container
    this.container.style.cssText = generateCSSVariables(this.config.branding || {});
    this.container.classList.add('luqta-sdk');

    if (this.config.rtl) {
      this.container.setAttribute('dir', 'rtl');
    }

    // Set contests directly
    this.state.contests = contests;
    this.state.error = null;
    this.state.loading = false;

    // Render the contest list (or empty state if no contests)
    this.renderContestList();
  }

  /**
   * Refresh the contest list
   */
  async refresh(): Promise<void> {
    await this.loadContests();
  }

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  private async loadContests(): Promise<void> {
    this.state.loading = true;
    this.showLoading();

    try {
      // Use fetchContests with pagination (requires SDK to be initialized)
      const response = await this.client.fetchContests({ page: 1, per_page: 10 });

      if (response.data?.items) {
        this.state.contests = response.data.items;
      } else if (Array.isArray(response.data)) {
        this.state.contests = response.data;
      } else {
        this.state.contests = [];
      }

      this.state.error = null;
      this.renderContestList();
    } catch (error) {
      // Check for NO_CONTESTS_FOUND error
      if (error instanceof LuqtaError && error.code === 'NO_CONTESTS_FOUND') {
        this.state.contests = [];
        this.state.error = null;
        this.renderContestList(); // Will show empty state
      } else {
        this.state.error = error instanceof Error ? error.message : 'Failed to load contests';
        this.renderError();
      }

      if (this.config.onError && error instanceof LuqtaError) {
        this.config.onError(error);
      }
    } finally {
      this.state.loading = false;
    }
  }

  // ============================================================================
  // RENDERING METHODS
  // ============================================================================

  private showLoading(): void {
    this.container.innerHTML = `
      <div class="luqta-loading">
        <div class="luqta-spinner"></div>
        <span>${this.t.loading}</span>
      </div>
    `;
  }

  private renderError(): void {
    this.container.innerHTML = `
      <div class="luqta-empty">
        <div class="luqta-empty-icon">${ICONS.error}</div>
        <h3 class="luqta-empty-title">${this.t.error}</h3>
        <p class="luqta-empty-desc">${this.state.error}</p>
        <button class="luqta-btn luqta-btn-primary" onclick="this.closest('.luqta-sdk').luqtaRetry()">
          ${this.t.retry}
        </button>
      </div>
    `;

    (this.container as any).luqtaRetry = () => this.loadContests();
  }

  private renderContestList(): void {
    if (this.state.contests.length === 0) {
      this.renderEmptyState();
      return;
    }

    const showIcon = this.config.showProfileIcon && this.client.isInitialized();

    this.container.innerHTML = `
      <div class="luqta-content">
        <h2 class="luqta-section-title">${this.t.contests}</h2>
        <div class="luqta-contests-grid">
          ${this.state.contests.map(contest => this.renderContestCard(contest)).join('')}
        </div>
      </div>
      <div class="luqta-watermark">⚡ Powered by Luqta</div>
      ${showIcon ? `<button class="luqta-profile-icon" id="luqta-profile-icon-btn" aria-label="Profile">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
          <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
        </svg>
      </button>` : ''}
    `;

    // Add click handlers for contest cards - show info modal (without requiring participation)
    this.container.querySelectorAll('.luqta-contest-card').forEach((card, index) => {
      card.addEventListener('click', (e) => {
        // Ignore clicks on info button or join button
        if ((e.target as HTMLElement).closest('.luqta-contest-info-btn') ||
            (e.target as HTMLElement).closest('.luqta-contest-join-btn')) {
          return;
        }
        const contest = this.state.contests[index];
        const contestType = contest.type || 'public';
        this.showContestInfoModal(contest, contestType === 'private');
      });
    });

    // Add click handlers for info buttons - same as card click
    this.container.querySelectorAll('.luqta-contest-info-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const contestId = parseInt(btn.getAttribute('data-contest-id') || '0');
        const contestType = btn.getAttribute('data-contest-type') || 'public';
        const contest = this.state.contests.find(c => c.id === contestId);
        if (contest) {
          this.showContestInfoModal(contest, contestType === 'private');
        }
      });
    });

    // Add click handlers for join buttons - check participation and join
    this.container.querySelectorAll('.luqta-contest-join-btn').forEach((btn, index) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.onContestClick(this.state.contests[index]);
      });
    });

    // Profile icon click + drag
    const profileIconBtn = this.container.querySelector('#luqta-profile-icon-btn') as HTMLElement | null;
    if (profileIconBtn) {
      this.initProfileIconDrag(profileIconBtn);
      profileIconBtn.addEventListener('click', () => this.openProfileSheet());
    }

    // Initialize carousels for each contest card
    this.initializeCarousels();
  }

  private initProfileIconDrag(el: HTMLElement): void {
    let isDragging = false;
    let startX = 0, startY = 0;
    let origRight = 0, origBottom = 0;

    el.addEventListener('mousedown', (e) => {
      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;
      const style = window.getComputedStyle(el);
      origRight = parseInt(style.right) || 16;
      origBottom = parseInt(style.bottom) || 100;
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const newRight = Math.max(8, origRight - dx);
      const newBottom = Math.max(8, origBottom - dy);
      el.style.right = `${newRight}px`;
      el.style.bottom = `${newBottom}px`;
    });

    document.addEventListener('mouseup', () => { isDragging = false; });

    // Touch support
    el.addEventListener('touchstart', (e) => {
      isDragging = true;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      const style = window.getComputedStyle(el);
      origRight = parseInt(style.right) || 16;
      origBottom = parseInt(style.bottom) || 100;
    }, { passive: true });

    document.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      el.style.right = `${Math.max(8, origRight - dx)}px`;
      el.style.bottom = `${Math.max(8, origBottom - dy)}px`;
    }, { passive: true });

    document.addEventListener('touchend', () => { isDragging = false; });
  }

  private async openProfileSheet(): Promise<void> {
    let profileData: any = null;
    try {
      profileData = await this.client.profile.get();
    } catch (_) {}

    this.state.profileData = profileData;
    this.state.showProfileSheet = true;
    this.renderProfileSheet(profileData);
  }

  private renderProfileSheet(profileData: any): void {
    // Remove existing sheet
    const existing = document.getElementById('luqta-profile-sheet-overlay');
    if (existing) existing.remove();

    const user = profileData?.data?.user;
    const stats = profileData?.data?.statistics;
    const primaryColor = this.config.branding?.primaryColor || '#5304fb';

    const statCards = stats ? `
      <p class="luqta-profile-stats-title">${this.t.profileStatistics}</p>
      <div class="luqta-profile-stats-grid">
        <div class="luqta-profile-stat-card">
          <span class="luqta-profile-stat-value">${stats.total_contests_joined ?? 0}</span>
          <span class="luqta-profile-stat-label">${this.t.profileContestsJoined}</span>
        </div>
        <div class="luqta-profile-stat-card">
          <span class="luqta-profile-stat-value">${stats.completed_contests ?? 0}</span>
          <span class="luqta-profile-stat-label">${this.t.profileCompleted}</span>
        </div>
        <div class="luqta-profile-stat-card">
          <span class="luqta-profile-stat-value">${stats.in_progress_contests ?? 0}</span>
          <span class="luqta-profile-stat-label">${this.t.profileInProgress}</span>
        </div>
        <div class="luqta-profile-stat-card">
          <span class="luqta-profile-stat-value">${stats.total_levels_completed ?? 0}</span>
          <span class="luqta-profile-stat-label">${this.t.profileLevelsCompleted}</span>
        </div>
      </div>
    ` : '';

    const pointsBadge = user?.total_points != null ? `
      <div class="luqta-profile-points-badge">
        <span class="luqta-profile-points-value">${user.total_points}</span>
        <span class="luqta-profile-points-label">${this.t.profileTotalPoints}</span>
      </div>
    ` : '';

    const verifiedBadge = user?.verified ? `<span class="luqta-profile-verified">✓ ${this.t.profileVerified}</span>` : '';

    const overlay = document.createElement('div');
    overlay.id = 'luqta-profile-sheet-overlay';
    overlay.className = 'luqta-profile-overlay';
    overlay.innerHTML = `
      <div class="luqta-profile-sheet" style="--luqta-profile-primary: ${primaryColor}">
        <div class="luqta-profile-handle"></div>
        <div class="luqta-profile-header">
          <div class="luqta-profile-avatar">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="30" height="30">
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
            </svg>
          </div>
          <div class="luqta-profile-info">
            <span class="luqta-profile-name">${user?.name ?? '—'}</span>
            <span class="luqta-profile-contact">${user?.phone_number ?? user?.email ?? ''}</span>
          </div>
          ${verifiedBadge}
        </div>
        <hr class="luqta-profile-divider" />
        ${pointsBadge}
        ${statCards}
      </div>
    `;

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.remove();
        this.state.showProfileSheet = false;
      }
    });

    document.body.appendChild(overlay);
  }

  /**
   * Initialize carousel auto-rotation and dot click handlers
   */
  private initializeCarousels(): void {
    const carousels = this.container.querySelectorAll('.luqta-contest-banner-carousel');

    carousels.forEach((carousel) => {
      const slides = carousel.querySelectorAll('.luqta-contest-banner-slide');
      if (slides.length <= 1) return;

      const card = carousel.closest('.luqta-contest-card');
      const dots = card?.querySelectorAll('.luqta-carousel-dot');
      let currentIndex = 0;
      let intervalId: number | null = null;

      const goToSlide = (index: number) => {
        // Remove active from all slides and dots
        slides.forEach(slide => slide.classList.remove('active'));
        dots?.forEach(dot => dot.classList.remove('active'));

        // Add active to current slide and dot
        slides[index]?.classList.add('active');
        dots?.[index]?.classList.add('active');

        currentIndex = index;
      };

      const nextSlide = () => {
        const nextIndex = (currentIndex + 1) % slides.length;
        goToSlide(nextIndex);
      };

      // Auto-rotate every 4 seconds
      const startAutoRotate = () => {
        if (intervalId) clearInterval(intervalId);
        intervalId = window.setInterval(nextSlide, 4000);
      };

      const stopAutoRotate = () => {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      };

      // Dot click handlers
      dots?.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          e.stopPropagation();
          goToSlide(index);
          // Restart auto-rotate after manual selection
          startAutoRotate();
        });
      });

      // Pause on hover
      card?.addEventListener('mouseenter', stopAutoRotate);
      card?.addEventListener('mouseleave', startAutoRotate);

      // Start auto-rotation
      startAutoRotate();
    });
  }

  private renderEmptyState(): void {
    this.container.innerHTML = `
      <div class="luqta-empty">
        <div class="luqta-empty-icon">${ICONS.empty}</div>
        <h3 class="luqta-empty-title">${this.t.noContests}</h3>
        <p class="luqta-empty-desc">${this.t.noContestsDesc}</p>
      </div>
    `;
  }

  private renderContestCard(contest: Contest): string {
    const userLoggedIn = this.client.isInitialized();
    const isCompleted = userLoggedIn && contest.participate_progress?.status === 'completed';
    const isParticipating = userLoggedIn && contest.participate_progress?.status === 'in_progress';

    // Get all banner URLs for carousel
    const banners = contest.banners?.filter(b => b.url) || [];

    // Get all company logos from user_ids (can be multiple companies)
    const companyLogos: string[] = [];
    if (contest.user_ids && contest.user_ids.length > 0) {
      contest.user_ids.forEach(userCompany => {
        const logos = userCompany.company?.business_logo;
        if (logos && logos.length > 0) {
          logos.forEach(logo => {
            if (logo.url) {
              companyLogos.push(logo.url);
            }
          });
        }
      });
    }

    const levelsCount = contest.levels?.length || 0;
    const timeLeft = this.calculateTimeLeft(contest.end_date);
    const contestTitle = this.config.locale === 'ar' && contest.arabic_name ? contest.arabic_name : contest.name;
    const contestDesc = this.config.locale === 'ar' && contest.arabic_details ? contest.arabic_details : contest.details;

    // Get prize info (first prize name and value)
    const prize = contest.prizes?.[0];
    const prizeText = prize
      ? `${this.t.win} ${prize.value.toLocaleString()} ${prize.name}`
      : `${contest.total_points} ${this.t.points}`;

    // Determine button state and text
    let buttonClass = '';
    let buttonText = this.t.join;
    if (isCompleted) {
      buttonClass = 'completed';
      buttonText = this.t.completed;
    } else if (isParticipating) {
      buttonClass = 'participating';
      buttonText = this.t.viewProgress;
    }

    // Render multiple company logos as avatar stack
    const logosHtml = companyLogos.length > 0 ? `
      <div class="luqta-contest-logos">
        ${companyLogos.map((logoUrl, index) => `
          <div class="luqta-contest-logo" style="z-index: ${companyLogos.length - index}; ${index > 0 ? `margin-left: -12px;` : ''}">
            <img src="${logoUrl}" alt="Company Logo">
          </div>
        `).join('')}
      </div>
    ` : '';

    // Render banner carousel (multiple banners with slides)
    const carouselHtml = banners.length > 0 ? `
      <div class="luqta-contest-banner-carousel" data-contest-id="${contest.id}">
        ${banners.map((banner, index) => `
          <div class="luqta-contest-banner-slide ${index === 0 ? 'active' : ''}" data-slide-index="${index}">
            <img src="${banner.url}" alt="${contestTitle}">
          </div>
        `).join('')}
      </div>
      ${banners.length > 1 ? `
        <div class="luqta-carousel-dots">
          ${banners.map((_, index) => `
            <div class="luqta-carousel-dot ${index === 0 ? 'active' : ''}" data-dot-index="${index}"></div>
          `).join('')}
        </div>
      ` : ''}
    ` : '';

    return `
      <div class="luqta-contest-card ${isCompleted ? 'completed' : ''}" data-contest-id="${contest.id}">
        <div class="luqta-contest-banner">
          ${carouselHtml}
          ${logosHtml}
          <div class="luqta-contest-badges">
            ${timeLeft ? `
              <span class="luqta-contest-badge timer">
                ${ICONS.clock} ${timeLeft} ${this.t.left}
              </span>
            ` : ''}
            ${levelsCount > 0 ? `
              <span class="luqta-contest-badge levels">
                ${ICONS.target} ${levelsCount} ${this.t.levels}
              </span>
            ` : ''}
            ${isCompleted ? `
              <span class="luqta-contest-badge completed-badge">
                ${ICONS.check} ${this.t.completed}
              </span>
            ` : ''}
          </div>
        </div>
        <div class="luqta-contest-body">
          <h3 class="luqta-contest-title">${prizeText}</h3>
          <p class="luqta-contest-desc">${contestDesc}</p>
          <div class="luqta-contest-footer">
            <button class="luqta-contest-info-btn" data-contest-id="${contest.id}" data-contest-type="${contest.type}" title="${this.t.contestInfo}">
              ${ICONS.info}
            </button>
            <button class="luqta-contest-join-btn ${buttonClass}">
              ${buttonText}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderContestDetail(contest: Contest, progressData?: any): void {
    this.state.selectedContest = contest;
    this.state.currentView = 'contest-detail';

    // Merge progress data if available (API response structure)
    if (progressData) {
      // Direct properties from API response
      if (progressData.levels) {
        contest.levels = progressData.levels;
      }
      if (progressData.contest_prizes) {
        contest.prizes = progressData.contest_prizes;
      }
      if (progressData.user_progress_of_contest) {
        contest.participate_progress = {
          participant_id: progressData.user_progress_of_contest.participant_id,
          status: progressData.user_progress_of_contest.status
        };
      }
      // Copy user_ids if present
      if (progressData.user_ids) {
        contest.user_ids = progressData.user_ids;
      }
    }

    const banners = contest.banners?.filter(b => b.url) || [];
    const isParticipating = contest.participate_progress?.status === 'in_progress';
    const isCompleted = contest.participate_progress?.status === 'completed';
    const levels = contest.levels || [];
    const prizes = contest.prizes || [];
    const timeLeft = this.calculateTimeLeft(contest.end_date);

    // Get company info from user_ids
    const companies = contest.user_ids || [];

    // Calculate progress
    const completedLevels = levels.filter(l => this.isLevelCompleted(l)).length;
    const progressPercentage = levels.length > 0 ? Math.round((completedLevels / levels.length) * 100) : 0;

    const contestTitle = this.config.locale === 'ar' && contest.arabic_name ? contest.arabic_name : contest.name;
    const contestDesc = this.config.locale === 'ar' && contest.arabic_details ? contest.arabic_details : contest.details;

    // Render banner carousel
    const carouselHtml = banners.length > 0 ? `
      <div class="luqta-detail-banner-carousel" id="luqta-detail-carousel">
        ${banners.map((banner, index) => `
          <div class="luqta-detail-banner-slide ${index === 0 ? 'active' : ''}" data-slide-index="${index}">
            <img src="${banner.url}" alt="${contestTitle}">
          </div>
        `).join('')}
      </div>
      ${banners.length > 1 ? `
        <div class="luqta-detail-carousel-dots">
          ${banners.map((_, index) => `
            <div class="luqta-detail-carousel-dot ${index === 0 ? 'active' : ''}" data-dot-index="${index}"></div>
          `).join('')}
        </div>
      ` : ''}
    ` : '';

    this.container.innerHTML = `
      <div class="luqta-contest-detail-v2">
        <!-- Banner Section with Carousel -->
        <div class="luqta-detail-banner">
          ${carouselHtml}
          <button class="luqta-back-btn" id="luqta-back">${ICONS.back}</button>
          <button class="luqta-share-btn" id="luqta-share">${ICONS.share}</button>
        </div>

        <!-- Content Section -->
        <div class="luqta-detail-content">
          <!-- Title & Description -->
          <div class="luqta-detail-header">
            <h1 class="luqta-detail-title">${contestTitle}</h1>
            <p class="luqta-detail-desc">
              ${contestDesc.length > 150 ? contestDesc.substring(0, 150) + '...' : contestDesc}
              ${contestDesc.length > 150 ? `<a href="#" class="luqta-see-more">${this.t.seeMore}</a>` : ''}
            </p>
          </div>

          <!-- Progress Section (only show when user is logged in) -->
          ${this.client.isInitialized() && (isParticipating || isCompleted) ? `
            <div class="luqta-detail-progress-section">
              <h3 class="luqta-detail-section-title">${this.t.progress}</h3>
              <div class="luqta-detail-progress-badges">
                ${timeLeft ? `
                  <span class="luqta-detail-badge timer">
                    ${ICONS.clock} ${timeLeft} ${this.t.left}
                  </span>
                ` : ''}
                <span class="luqta-detail-badge levels">
                  ${ICONS.target} ${levels.length} ${this.t.levels}
                </span>
              </div>
              <p class="luqta-detail-progress-hint">${this.t.completeAllLevels}</p>
              <div class="luqta-detail-progress-bar">
                <div class="luqta-detail-progress-fill" style="width: ${progressPercentage}%"></div>
                ${isCompleted ? `<span class="luqta-progress-check">${ICONS.check}</span>` : ''}
              </div>
            </div>
          ` : ''}

          <!-- Levels Section (only show when user is logged in) -->
          ${this.client.isInitialized() && (isParticipating || isCompleted) && levels.length > 0 ? `
            <div class="luqta-detail-levels-section">
              <h3 class="luqta-detail-section-title">${this.t.levels}</h3>
              <div class="luqta-detail-levels-list">
                ${levels.map((level, index) => this.renderDetailLevelCard(level, index, levels, isCompleted)).join('')}
              </div>
            </div>
          ` : ''}

          <!-- Companies Section -->
          ${companies.length > 0 ? `
            <div class="luqta-detail-companies-section">
              <h3 class="luqta-detail-section-title">${this.t.aboutCompany}</h3>
              <div class="luqta-detail-companies-scroll">
                ${companies.map(item => this.renderCompanyCard(item.company)).join('')}
              </div>
            </div>
          ` : ''}

          <!-- Prizes Section -->
          ${prizes.length > 0 ? `
            <div class="luqta-detail-prizes-section">
              <h3 class="luqta-detail-section-title">${this.t.prizes}</h3>
              <div class="luqta-detail-prizes-list">
                ${prizes.map(prize => this.renderPrizeCard(prize)).join('')}
              </div>
            </div>
          ` : ''}
        </div>
      </div>
    `;

    // Back button handler
    document.getElementById('luqta-back')?.addEventListener('click', () => {
      this.state.currentView = 'contests';
      this.state.selectedContest = null;
      this.renderContestList();
    });

    // Share button handler
    document.getElementById('luqta-share')?.addEventListener('click', () => {
      if (navigator.share) {
        navigator.share({
          title: contestTitle,
          text: contestDesc,
          url: window.location.href
        }).catch(() => { });
      }
    });

    // Level card Play button handlers
    this.container.querySelectorAll('.luqta-detail-level-play').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const levelId = parseInt(btn.getAttribute('data-level-id') || '0');
        const level = levels.find(l => l.id === levelId);
        if (level) {
          this.showLevelModal(level);
        }
      });
    });

    // Initialize detail page carousel
    this.initializeDetailCarousel();
  }

  /**
   * Renders a level card for the detail page (Figma design)
   */
  private renderDetailLevelCard(level: Level | LevelWithProgress, index: number, allLevels: (Level | LevelWithProgress)[], _isContestCompleted: boolean): string {
    // Use individual level's completion status, NOT contest-level completion
    // This ensures levels with status "in_progress" show as playable even if contest is "completed"
    const isCompleted = this.isLevelCompleted(level);

    // Previous level check for determining playable/locked state
    const previousLevel = index > 0 ? allLevels[index - 1] : null;
    const isPreviousCompleted = previousLevel ? this.isLevelCompleted(previousLevel) : true;

    // Level is locked if it's not completed AND previous level is not completed
    // Otherwise, if not completed but previous is done (or first level), it's playable
    const isLocked = !isCompleted && !isPreviousCompleted;

    const levelName = this.config.locale === 'ar' && level.arabic_name ? level.arabic_name : level.name;
    const levelDesc = this.config.locale === 'ar' && level.arabic_description ? level.arabic_description : level.description;
    const levelTypeIcon = getLevelTypeIcon(level.level_type);

    // Status and styling
    let statusClass = '';
    let statusHtml = '';
    let actionHtml = '';

    if (isCompleted) {
      statusClass = 'completed';
      statusHtml = `<span class="luqta-detail-level-status completed">${this.t.completed} • +${level.points}${this.t.points}</span>`;
      actionHtml = `<span class="luqta-detail-level-check">${ICONS.check}</span>`;
    } else if (isLocked) {
      statusClass = 'locked';
      statusHtml = `<span class="luqta-detail-level-status">${this.t.taskDescription}</span>`;
      actionHtml = `<span class="luqta-detail-level-locked">${ICONS.lock}</span>`;
    } else {
      statusClass = 'playable';
      statusHtml = `<span class="luqta-detail-level-status">${this.t.taskDescription}</span>`;
      actionHtml = `<button class="luqta-detail-level-play" data-level-id="${level.id}">${this.t.play}</button>`;
    }

    return `
      <div class="luqta-detail-level-card ${statusClass}" data-level-id="${level.id}">
        <div class="luqta-detail-level-icon ${level.level_type}">
          ${levelTypeIcon}
        </div>
        <div class="luqta-detail-level-info">
          <h4 class="luqta-detail-level-name">${levelName}</h4>
          ${statusHtml}
        </div>
        <div class="luqta-detail-level-action">
          ${actionHtml}
        </div>
      </div>
    `;
  }

  /**
   * Renders a company card for the detail page
   */
  private renderCompanyCard(company: any): string {
    if (!company) return '';

    const logoUrl = company.business_logo?.[0]?.url;
    const companyName = company.name || '';
    const companyDetails = company.details || '';

    return `
      <div class="luqta-detail-company-card">
        ${logoUrl ? `
          <div class="luqta-detail-company-logo">
            <img src="${logoUrl}" alt="${companyName}">
          </div>
        ` : `
          <div class="luqta-detail-company-logo placeholder">
            ${ICONS.building}
          </div>
        `}
        <div class="luqta-detail-company-info">
          <h4 class="luqta-detail-company-name">${companyName}</h4>
          ${companyDetails ? `<p class="luqta-detail-company-desc">${companyDetails}</p>` : ''}
        </div>
      </div>
    `;
  }

  /**
   * Renders a prize card for the detail page
   */
  private renderPrizeCard(prize: any): string {
    const prizeImage = prize.image?.url;
    const prizeName = prize.name || '';
    const prizeDesc = prize.description || '';

    return `
      <div class="luqta-detail-prize-card">
        ${prizeImage ? `
          <div class="luqta-detail-prize-image">
            <img src="${prizeImage}" alt="${prizeName}">
          </div>
        ` : `
          <div class="luqta-detail-prize-image placeholder">
            ${ICONS.gift}
          </div>
        `}
        <div class="luqta-detail-prize-info">
          <h4 class="luqta-detail-prize-name">${prizeName}</h4>
          ${prizeDesc ? `<p class="luqta-detail-prize-desc">${prizeDesc}</p>` : ''}
        </div>
      </div>
    `;
  }

  /**
   * Initialize carousel for detail page banner
   */
  private initializeDetailCarousel(): void {
    const carousel = document.getElementById('luqta-detail-carousel');
    if (!carousel) return;

    const slides = carousel.querySelectorAll('.luqta-detail-banner-slide');
    if (slides.length <= 1) return;

    const dotsContainer = this.container.querySelector('.luqta-detail-carousel-dots');
    const dots = dotsContainer?.querySelectorAll('.luqta-detail-carousel-dot');
    let currentIndex = 0;
    let intervalId: number | null = null;

    const goToSlide = (index: number) => {
      slides.forEach(slide => slide.classList.remove('active'));
      dots?.forEach(dot => dot.classList.remove('active'));

      slides[index]?.classList.add('active');
      dots?.[index]?.classList.add('active');
      currentIndex = index;
    };

    const nextSlide = () => {
      const nextIndex = (currentIndex + 1) % slides.length;
      goToSlide(nextIndex);
    };

    const startAutoRotate = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = window.setInterval(nextSlide, 4000);
    };

    const stopAutoRotate = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Dot click handlers
    dots?.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        goToSlide(index);
        startAutoRotate();
      });
    });

    // Pause on hover
    carousel.addEventListener('mouseenter', stopAutoRotate);
    carousel.addEventListener('mouseleave', startAutoRotate);

    // Start auto-rotation
    startAutoRotate();
  }

  private renderLevelsList(levels: (Level | LevelWithProgress)[], isContestCompleted: boolean): string {
    if (levels.length === 0) {
      return '';
    }

    // Calculate completed levels count
    const completedCount = levels.filter(l => this.isLevelCompleted(l)).length;

    return `
      <div class="luqta-levels-section">
        <h3 class="luqta-levels-title">
          ${ICONS.target} ${this.t.levels} (${completedCount}/${levels.length})
        </h3>
        <div class="luqta-levels-list">
          ${levels.map((level, index) => this.renderLevelCard(level, index, levels, isContestCompleted)).join('')}
        </div>
      </div>
    `;
  }

  private renderLevelCard(level: Level | LevelWithProgress, index: number, allLevels: (Level | LevelWithProgress)[], _isContestCompleted: boolean): string {
    // Use individual level's completion status, NOT contest-level completion
    // This ensures levels with status "in_progress" show as playable even if contest is "completed"
    const isCompleted = this.isLevelCompleted(level);

    // Determine if locked (previous level not completed)
    const previousLevel = index > 0 ? allLevels[index - 1] : null;
    const isPreviousCompleted = previousLevel ? this.isLevelCompleted(previousLevel) : true;
    const isLocked = !isCompleted && !isPreviousCompleted;

    // Level is in-progress if it's not completed and not locked (i.e., playable)
    const isInProgress = !isCompleted && !isLocked;

    const statusClass = isCompleted ? 'completed' : (isInProgress ? 'in-progress' : (isLocked ? 'locked' : ''));
    const icon = isCompleted ? ICONS.check : getLevelTypeIcon(level.level_type);
    const levelName = this.config.locale === 'ar' && level.arabic_name ? level.arabic_name : level.name;
    const levelDesc = this.config.locale === 'ar' && level.arabic_description ? level.arabic_description : level.description;

    // Show status badge
    const statusBadge = isCompleted
      ? `<span class="luqta-level-status completed">${this.t.completed}</span>`
      : isInProgress
        ? `<span class="luqta-level-status in-progress">${this.t.inProgress}</span>`
        : '';

    return `
      <div class="luqta-level-card ${statusClass}" data-level-id="${level.id}" data-level-type="${level.level_type}" data-quiz-id="${level.quizzes_id || ''}">
        <div class="luqta-level-icon">${icon}</div>
        <div class="luqta-level-info">
          <h4 class="luqta-level-name">${levelName}</h4>
          <p class="luqta-level-desc">${levelDesc}</p>
          <div class="luqta-level-meta">
            <span class="luqta-level-type">
              ${getLevelTypeIcon(level.level_type)} ${getLevelTypeName(level.level_type)}
            </span>
            ${statusBadge}
          </div>
        </div>
        <div class="luqta-level-points">
          ${isLocked ? ICONS.lock : `+${level.points}`}
        </div>
      </div>
    `;
  }

  // ============================================================================
  // MODALS
  // ============================================================================

  private showParticipateModal(contest: Contest): void {
    const modalHtml = `
      <div class="luqta-modal-overlay" id="luqta-modal">
        <div class="luqta-modal">
          <div class="luqta-modal-header">
            <h3 class="luqta-modal-title">${this.t.joinToParticipate}</h3>
            <button class="luqta-modal-close" id="luqta-modal-close">${ICONS.close}</button>
          </div>
          <div class="luqta-modal-body">
            <div class="luqta-participate">
              <div class="luqta-participate-icon">${ICONS.trophy}</div>
              <h4 class="luqta-participate-title">${this.t.participateTitle}</h4>
              <p class="luqta-participate-desc">${this.t.participateDesc}</p>
            </div>
          </div>
          <div class="luqta-modal-footer">
            <button class="luqta-btn luqta-btn-outline" id="luqta-cancel">${this.t.cancel}</button>
            <button class="luqta-btn luqta-btn-primary" id="luqta-participate">
              ${this.t.participate}
            </button>
          </div>
        </div>
      </div>
    `;

    this.container.insertAdjacentHTML('beforeend', modalHtml);
    this.setupModalHandlers(contest);
  }

  /**
   * Shows contest info page with details fetched from API
   */
  private showContestInfoModal(contest: Contest, isPrivate: boolean): void {
    // For private contests, check if we have cached access code in localStorage
    if (isPrivate) {
      const cachedAccessCode = this.getAccessCodeFromCache(contest.id);

      // If no cached code, show access code modal first
      if (!cachedAccessCode) {
        this.showAccessCodeModal(contest);
        return;
      }

      // Have cached code, call API with it
      this.showLoading();
      this.client.getContestDetails(contest.id, cachedAccessCode)
        .then((response) => {
          if (response.success && response.data) {
            const data = response.data;
            const levelProgress = this.extractLevelProgressFromResponse(data);
            const isParticipating = levelProgress.length > 0 || this.checkUserParticipation(data);
            this.renderContestInfoPage(data, isParticipating, levelProgress);
          } else {
            this.showToast('error', this.t.error);
            this.renderContestList();
          }
        })
        .catch(() => {
          // Invalid cached code, clear it and show access code modal
          this.removeAccessCodeFromCache(contest.id);
          this.renderContestList();
          this.showAccessCodeModal(contest);
        });
      return;
    }

    // For public contests, just call API directly
    this.showLoading();
    this.client.getContestDetails(contest.id)
      .then((response) => {
        if (response.success && response.data) {
          const data = response.data;
          const levelProgress = this.extractLevelProgressFromResponse(data);
          const isParticipating = levelProgress.length > 0 || this.checkUserParticipation(data);
          this.renderContestInfoPage(data, isParticipating, levelProgress);
        } else {
          this.showToast('error', this.t.error);
          this.renderContestList();
        }
      })
      .catch((error) => {
        this.showToast('error', error.message || this.t.error);
        this.renderContestList();
      });
  }

  /**
   * Extract level progress from API response
   * The API returns level_progress array inside each level
   */
  private extractLevelProgressFromResponse(data: any): any[] {
    const allProgress: any[] = [];

    if (data.levels && Array.isArray(data.levels)) {
      data.levels.forEach((level: any) => {
        if (level.level_progress && Array.isArray(level.level_progress) && level.level_progress.length > 0) {
          level.level_progress.forEach((progress: any) => {
            allProgress.push({
              level_id: level.id,
              status: progress.status || 'completed',
              points_earned: progress.points_earned || level.points,
              ...progress
            });
          });
        }
      });
    }

    return allProgress;
  }

  /**
   * Check if user is participating based on API response
   */
  private checkUserParticipation(data: any): boolean {
    // Check if any level has progress
    if (data.user_progress_of_contest && data.user_progress_of_contest.status) {
      return true;
    }
    if (data.levels && Array.isArray(data.levels)) {
      for (const level of data.levels) {
        if (level.level_progress && Array.isArray(level.level_progress) && level.level_progress.length > 0) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Shows access code input modal for private contests
   */
  private showAccessCodeModal(contest: Contest): void {
    const modalHtml = `
      <div class="luqta-modal-overlay" id="luqta-modal">
        <div class="luqta-modal luqta-info-modal">
          <div class="luqta-modal-header">
            <h3 class="luqta-modal-title">${this.t.accessCodeRequired}</h3>
            <button class="luqta-modal-close" id="luqta-modal-close">${ICONS.close}</button>
          </div>
          <div class="luqta-modal-body">
            <div class="luqta-access-code-content">
              <div class="luqta-access-code-icon">
                ${ICONS.lock}
              </div>
              <p class="luqta-access-code-desc">${this.t.enterAccessCodeInfo}</p>
              <div class="luqta-input-group">
                <input type="text" class="luqta-input" id="luqta-access-code-input" placeholder="${this.t.accessCodePlaceholder}">
              </div>
            </div>
          </div>
          <div class="luqta-modal-footer">
            <button class="luqta-btn luqta-btn-outline" id="luqta-cancel">${this.t.cancel}</button>
            <button class="luqta-btn luqta-btn-primary" id="luqta-view-details">
              ${this.t.viewDetails}
            </button>
          </div>
        </div>
      </div>
    `;

    this.container.insertAdjacentHTML('beforeend', modalHtml);

    // Setup handlers
    document.getElementById('luqta-modal-close')?.addEventListener('click', () => this.closeModal());
    document.getElementById('luqta-cancel')?.addEventListener('click', () => this.closeModal());
    document.getElementById('luqta-modal')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).id === 'luqta-modal') {
        this.closeModal();
      }
    });

    document.getElementById('luqta-view-details')?.addEventListener('click', () => {
      const accessCode = (document.getElementById('luqta-access-code-input') as HTMLInputElement)?.value?.trim();
      this.closeModal();
      this.showLoading();

      this.client.getContestDetails(contest.id, accessCode || undefined)
        .then((response) => {
          if (response.success && response.data) {
            // Cache the access code in localStorage on success
            if (accessCode) {
              this.setAccessCodeToCache(contest.id, accessCode);
            }

            const data = response.data;
            const levelProgress = this.extractLevelProgressFromResponse(data);
            const isParticipating = levelProgress.length > 0 || this.checkUserParticipation(data);

            this.renderContestInfoPage(data, isParticipating, levelProgress);
          } else {
            this.showToast('error', this.t.error);
            this.renderContestList();
          }
        })
        .catch((error) => {
          // Clear invalid code from localStorage cache
          this.removeAccessCodeFromCache(contest.id);
          this.showToast('error', error.message || this.t.error);
          // Show access code modal again for retry
          this.renderContestList();
          this.showAccessCodeModal(contest);
        });
    });
  }

  /**
   * Renders the contest info as a full page with banners and company info
   * @param data - Contest details from API
   * @param isParticipating - Whether user is already participating (optional)
   * @param levelProgress - Level progress data if participating (optional)
   */
  private renderContestInfoPage(data: any, isParticipating: boolean = false, levelProgress: any[] = []): void {
    const contestTitle = this.config.locale === 'ar' && data.arabic_name ? data.arabic_name : data.name;
    const contestDesc = this.config.locale === 'ar' && data.arabic_details ? data.arabic_details : data.details;

    // Format dates
    const formatDate = (timestamp: number) => {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      return date.toLocaleDateString(this.config.locale === 'ar' ? 'ar-SA' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    // Gender display
    const genderDisplay = data.gender === 'both' ? this.t.bothGenders :
      data.gender === 'male' ? this.t.male :
        data.gender === 'female' ? this.t.female : data.gender;

    // Age groups
    const ageGroups = Array.isArray(data.age) ? data.age.join(', ') : (data.age || this.t.all);

    // Draw rules
    const drawRules = data.draw_rules;
    const hasDrawRules = drawRules && (drawRules.must_complete_profile || drawRules.must_complete_one_task || drawRules.enable_specific_number_of_points);

    // Levels summary
    const levelsCount = data.levels?.length || 0;
    const levels = data.levels || [];

    // Calculate completed levels
    const completedLevels = levelProgress.filter((lp: any) => lp.status === 'completed').length;
    const earnedPoints = levelProgress.reduce((sum: number, lp: any) => sum + (lp.points_earned || 0), 0);

    // Prizes summary
    const prizesCount = data.contest_prizes?.length || 0;
    const prizes = data.contest_prizes || [];

    // Banners
    const banners = data.banners?.filter((b: any) => b.url) || [];

    // Companies from user_ids
    const companies = data.user_ids || [];

    // Render banner carousel
    const carouselHtml = banners.length > 0 ? `
      <div class="luqta-detail-banner-carousel" id="luqta-info-carousel">
        ${banners.map((banner: any, index: number) => `
          <div class="luqta-detail-banner-slide ${index === 0 ? 'active' : ''}" data-slide-index="${index}">
            <img src="${banner.url}" alt="${contestTitle}">
          </div>
        `).join('')}
      </div>
      ${banners.length > 1 ? `
        <div class="luqta-detail-carousel-dots">
          ${banners.map((_: any, index: number) => `
            <div class="luqta-detail-carousel-dot ${index === 0 ? 'active' : ''}" data-dot-index="${index}"></div>
          `).join('')}
        </div>
      ` : ''}
    ` : '';

    // Check if level is completed
    const isLevelCompleted = (levelId: number) => {
      return levelProgress.some((lp: any) => lp.level_id === levelId && lp.status === 'completed');
    };

    // Check if level is available (unlocked)
    const isLevelAvailable = (level: any, index: number) => {
      // First level is always available
      if (index === 0) return true;
      // Check if level timeline has passed
      if (level.level_timeline && level.level_timeline > Date.now()) return false;
      // Check if previous level is completed (if sequential)
      const prevLevel = levels[index - 1];
      if (prevLevel && !isLevelCompleted(prevLevel.id)) return false;
      return true;
    };

    this.container.innerHTML = `
      <div class="luqta-contest-info-page">
        <!-- Banner Section with Carousel -->
        <div class="luqta-detail-banner">
          ${carouselHtml}
          <button class="luqta-back-btn" id="luqta-back">${ICONS.back}</button>
          <button class="luqta-share-btn" id="luqta-share">${ICONS.share}</button>
        </div>

        <!-- Content Section -->
        <div class="luqta-info-page-content">
          <!-- Title & Description -->
          <div class="luqta-info-page-header">
            <h1 class="luqta-info-page-title">${contestTitle}</h1>
            <p class="luqta-info-page-desc">${contestDesc}</p>

            <!-- Contest Type Badges -->
            <div class="luqta-info-badges">
              <span class="luqta-info-badge ${data.type}">
                ${data.type === 'public' ? ICONS.users : ICONS.lock}
                ${data.type === 'public' ? this.t.publicContest : this.t.privateContest}
              </span>
              ${data.View_Type ? `<span class="luqta-info-badge view-type">${data.View_Type}</span>` : ''}
              ${this.client.isInitialized() && isParticipating ? `<span class="luqta-info-badge participating">${ICONS.check} ${this.t.participating}</span>` : ''}
            </div>
          </div>

          <!-- Progress Section (only if participating and user is logged in) -->
          ${this.client.isInitialized() && isParticipating ? `
            <div class="luqta-info-page-section luqta-progress-section">
              <div class="luqta-info-progress-header">
                <h3 class="luqta-info-page-section-title">${this.t.yourProgress}</h3>
                <span class="luqta-info-progress-text">${completedLevels}/${levelsCount} ${this.t.levels}</span>
              </div>
              <div class="luqta-detail-progress-bar">
                <div class="luqta-detail-progress-fill" style="width: ${levelsCount > 0 ? (completedLevels / levelsCount) * 100 : 0}%"></div>
              </div>
              <div class="luqta-info-progress-stats">
                <span class="luqta-info-progress-points">${ICONS.star} ${earnedPoints} / ${data.total_points || 0} ${this.t.points}</span>
              </div>
            </div>
          ` : ''}

          <!-- Stats Row -->
          <div class="luqta-info-page-section">
            <div class="luqta-info-stats">
              <div class="luqta-info-stat">
                <span class="luqta-info-stat-value">${data.total_points || 0}</span>
                <span class="luqta-info-stat-label">${this.t.totalPointsLabel}</span>
              </div>
              <div class="luqta-info-stat">
                <span class="luqta-info-stat-value">${data.number_of_winners || 0}</span>
                <span class="luqta-info-stat-label">${this.t.numberOfWinners}</span>
              </div>
              <div class="luqta-info-stat">
                <span class="luqta-info-stat-value">${levelsCount}</span>
                <span class="luqta-info-stat-label">${this.t.levels}</span>
              </div>
              <div class="luqta-info-stat">
                <span class="luqta-info-stat-value">${data.no_of_users || 0}</span>
                <span class="luqta-info-stat-label">${this.t.participants}</span>
              </div>
            </div>
          </div>

          <!-- Contest Details Grid -->
          <div class="luqta-info-page-section">
            <h3 class="luqta-info-page-section-title">${this.t.contestInfo}</h3>
            <div class="luqta-info-grid">
              <div class="luqta-info-item">
                <span class="luqta-info-icon">${ICONS.calendar}</span>
                <div class="luqta-info-detail">
                  <span class="luqta-info-label">${this.t.startDateLabel}</span>
                  <span class="luqta-info-value">${formatDate(data.start_date)}</span>
                </div>
              </div>
              <div class="luqta-info-item">
                <span class="luqta-info-icon">${ICONS.calendar}</span>
                <div class="luqta-info-detail">
                  <span class="luqta-info-label">${this.t.endDateLabel}</span>
                  <span class="luqta-info-value">${formatDate(data.end_date)}</span>
                </div>
              </div>
              ${data.prize_announce_date ? `
                <div class="luqta-info-item">
                  <span class="luqta-info-icon">${ICONS.gift}</span>
                  <div class="luqta-info-detail">
                    <span class="luqta-info-label">${this.t.prizeAnnounceDate}</span>
                    <span class="luqta-info-value">${formatDate(data.prize_announce_date)}</span>
                  </div>
                </div>
              ` : ''}
              <div class="luqta-info-item">
                <span class="luqta-info-icon">${ICONS.location}</span>
                <div class="luqta-info-detail">
                  <span class="luqta-info-label">${this.t.locationLabel}</span>
                  <span class="luqta-info-value">${data.location || '-'}</span>
                </div>
              </div>
              <div class="luqta-info-item">
                <span class="luqta-info-icon">${ICONS.gender}</span>
                <div class="luqta-info-detail">
                  <span class="luqta-info-label">${this.t.genderLabel}</span>
                  <span class="luqta-info-value">${genderDisplay}</span>
                </div>
              </div>
              <div class="luqta-info-item">
                <span class="luqta-info-icon">${ICONS.users}</span>
                <div class="luqta-info-detail">
                  <span class="luqta-info-label">${this.t.ageGroups}</span>
                  <span class="luqta-info-value">${ageGroups}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Companies Section -->
          ${companies.length > 0 ? `
            <div class="luqta-info-page-section">
              <h3 class="luqta-info-page-section-title">${this.t.aboutCompany}</h3>
              <div class="luqta-detail-companies-scroll">
                ${companies.map((item: any) => this.renderCompanyCard(item.company)).join('')}
              </div>
            </div>
          ` : ''}

          <!-- Levels Section -->
          ${levelsCount > 0 ? `
            <div class="luqta-info-page-section">
              <h3 class="luqta-info-page-section-title">${this.t.levels} (${levelsCount})</h3>
              <div class="luqta-info-levels-list">
                ${levels.map((level: any, index: number) => {
      const levelName = this.config.locale === 'ar' && level.arabic_name ? level.arabic_name : level.name;
      const levelDesc = this.config.locale === 'ar' && level.arabic_description ? level.arabic_description : level.description;
      const userLoggedIn = this.client.isInitialized();
      const completed = userLoggedIn && isLevelCompleted(level.id);
      const available = userLoggedIn && isParticipating && isLevelAvailable(level, index);
      const locked = userLoggedIn && isParticipating && !available && !completed;

      return `
                    <div class="luqta-info-level-card ${completed ? 'completed' : ''} ${locked ? 'locked' : ''} ${available && !completed ? 'clickable' : ''}"
                         data-level-id="${level.id}"
                         data-level-available="${available && !completed}">
                      <div class="luqta-info-level-icon ${level.level_type} ${completed ? 'completed' : ''}">
                        ${completed ? ICONS.check : getLevelTypeIcon(level.level_type)}
                      </div>
                      <div class="luqta-info-level-info">
                        <h4 class="luqta-info-level-title">${levelName}</h4>
                        <p class="luqta-info-level-desc">${levelDesc || ''}</p>
                        ${completed ? `<span class="luqta-info-level-status completed">${this.t.completed}</span>` : ''}
                        ${locked ? `<span class="luqta-info-level-status locked">${ICONS.lock} ${this.t.locked}</span>` : ''}
                      </div>
                      <div class="luqta-info-level-action">
                        ${completed ? `
                          <div class="luqta-info-level-check">${ICONS.check}</div>
                        ` : available ? `
                          <button class="luqta-info-level-play-btn">${this.t.play}</button>
                        ` : `
                          <div class="luqta-info-level-points-badge">+${level.points}</div>
                        `}
                      </div>
                    </div>
                  `;
    }).join('')}
              </div>
            </div>
          ` : ''}

          <!-- Prizes Section -->
          ${prizesCount > 0 ? `
            <div class="luqta-info-page-section">
              <h3 class="luqta-info-page-section-title">${this.t.prizes} (${prizesCount})</h3>
              <div class="luqta-detail-prizes-list">
                ${prizes.map((prize: any) => `
                  <div class="luqta-detail-prize-card">
                    ${prize.image?.url ? `
                      <div class="luqta-detail-prize-image">
                        <img src="${prize.image.url}" alt="${prize.name}">
                      </div>
                    ` : `
                      <div class="luqta-detail-prize-image placeholder">
                        ${ICONS.gift}
                      </div>
                    `}
                    <div class="luqta-detail-prize-info">
                      <h4 class="luqta-detail-prize-name">${prize.name}</h4>
                      ${prize.description ? `<p class="luqta-detail-prize-desc">${prize.description}</p>` : ''}
                      ${prize.value ? `<span class="luqta-detail-prize-value">${prize.value}</span>` : ''}
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}

          <!-- Draw Rules -->
          ${hasDrawRules ? `
            <div class="luqta-info-page-section">
              <h3 class="luqta-info-page-section-title">${this.t.drawRules}</h3>
              <div class="luqta-info-rules-card">
                ${drawRules.must_complete_profile ? `
                  <div class="luqta-info-rule">
                    ${ICONS.check} ${this.t.mustCompleteProfile}
                  </div>
                ` : ''}
                ${drawRules.must_complete_one_task ? `
                  <div class="luqta-info-rule">
                    ${ICONS.check} ${this.t.mustCompleteOneTask}
                  </div>
                ` : ''}
                ${drawRules.enable_specific_number_of_points ? `
                  <div class="luqta-info-rule">
                    ${ICONS.check} ${this.t.specificPointsRequired}: ${drawRules.specific_number_of_points}
                  </div>
                ` : ''}
              </div>
            </div>
          ` : ''}
        </div>

        <!-- Fixed Bottom Action Button (only show if not participating) -->
        ${!isParticipating ? `
          <div class="luqta-info-page-footer">
            <button class="luqta-btn luqta-btn-primary luqta-btn-block" id="luqta-participate-btn">
              ${this.t.participate}
            </button>
          </div>
        ` : ''}
      </div>
    `;

    // Store contest data for later use
    this.state.currentInfoPageData = data;

    // Back button handler
    document.getElementById('luqta-back')?.addEventListener('click', () => {
      this.renderContestList();
    });

    // Share button handler
    document.getElementById('luqta-share')?.addEventListener('click', () => {
      if (navigator.share) {
        navigator.share({
          title: contestTitle,
          text: contestDesc,
          url: data.link || window.location.href
        }).catch(() => { });
      }
    });

    // Participate button handler (only when not participating)
    if (!isParticipating) {
      document.getElementById('luqta-participate-btn')?.addEventListener('click', () => {
        this.handleInfoPageParticipation(data);
      });
    }

    // Level click handlers (only for available levels)
    if (isParticipating) {
      this.container.querySelectorAll('.luqta-info-level-card[data-level-available="true"]').forEach((card) => {
        card.addEventListener('click', () => {
          const levelId = parseInt(card.getAttribute('data-level-id') || '0');
          const level = levels.find((l: any) => l.id === levelId);
          if (level) {
            // Store current contest in state for returning after level completion
            this.state.currentContest = {
              id: data.id,
              name: data.name,
              arabic_name: data.arabic_name,
              details: data.details,
              arabic_details: data.arabic_details,
              type: data.type,
              banners: data.banners,
              levels: levels,
              total_points: data.total_points,
              isParticipating: true
            } as any;

            this.showLevelModal(level);
          }
        });
      });
    }

    // Initialize banner carousel
    this.initializeInfoPageCarousel();
  }

  /**
   * Handle participation from info page
   */
  private async handleInfoPageParticipation(contestData: any): Promise<void> {
    // Check if user is initialized
    if (!this.client.isInitialized()) {
      this.showToast('error', this.t.userNotInitialized);
      return;
    }

    // Contest availability: block join when start_date is in the future
    // or end_date has already passed. Only runs for users who haven't
    // participated yet (handler is only wired when isParticipating is false).
    const now = new Date();
    if (contestData?.start_date) {
      const contestStart = this.parseLevelDate(contestData.start_date);
      if (contestStart && contestStart > now) {
        this.showUnavailableDialog(this.t.contestUnavailableTitle, this.t.contestNotStarted);
        return;
      }
    }
    if (contestData?.end_date) {
      const contestEnd = this.parseLevelDate(contestData.end_date);
      if (contestEnd && contestEnd < now) {
        this.showUnavailableDialog(this.t.contestUnavailableTitle, this.t.contestEndedMessage);
        return;
      }
    }

    const participateBtn = document.getElementById('luqta-participate-btn');
    if (participateBtn) {
      participateBtn.setAttribute('disabled', 'true');
      participateBtn.innerHTML = `<span class="luqta-spinner-small"></span> ${this.t.loading}`;
    }

    try {
      // For private contests, we might need access code
      const accessCode = contestData.type === 'private' ? contestData.access_code : undefined;

      const response = await this.client.participateContest(contestData.id, accessCode);

      if (response.success) {
        this.showToast('success', this.t.participationSuccess);

        // Re-fetch contest details to get updated progress
        try {
          const accessCode = contestData.type === 'private' ? contestData.access_code : undefined;
          const detailsResponse = await this.client.getContestDetails(contestData.id, accessCode);

          if (detailsResponse.success && detailsResponse.data) {
            const data = detailsResponse.data;
            const levelProgress = this.extractLevelProgressFromResponse(data);
            // Re-render the page with participation state
            this.renderContestInfoPage(data, true, levelProgress);
          } else {
            // Re-render with participation but no progress data
            this.renderContestInfoPage(contestData, true, []);
          }
        } catch {
          // Re-render with participation but no progress data
          this.renderContestInfoPage(contestData, true, []);
        }
      } else {
        this.showToast('error', response.error?.message || this.t.error);
        if (participateBtn) {
          participateBtn.removeAttribute('disabled');
          participateBtn.innerHTML = this.t.participate;
        }
      }
    } catch (error: any) {
      this.showToast('error', error.message || this.t.error);
      if (participateBtn) {
        participateBtn.removeAttribute('disabled');
        participateBtn.innerHTML = this.t.participate;
      }
    }
  }

  /**
   * Initialize carousel for info page banner
   */
  private initializeInfoPageCarousel(): void {
    const carousel = document.getElementById('luqta-info-carousel');
    if (!carousel) return;

    const slides = carousel.querySelectorAll('.luqta-detail-banner-slide');
    if (slides.length <= 1) return;

    const dotsContainer = this.container.querySelector('.luqta-detail-carousel-dots');
    const dots = dotsContainer?.querySelectorAll('.luqta-detail-carousel-dot');
    let currentIndex = 0;
    let intervalId: number | null = null;

    const goToSlide = (index: number) => {
      slides.forEach(slide => slide.classList.remove('active'));
      dots?.forEach(dot => dot.classList.remove('active'));

      slides[index]?.classList.add('active');
      dots?.[index]?.classList.add('active');
      currentIndex = index;
    };

    const nextSlide = () => {
      const nextIndex = (currentIndex + 1) % slides.length;
      goToSlide(nextIndex);
    };

    const startAutoRotate = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = window.setInterval(nextSlide, 4000);
    };

    const stopAutoRotate = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Dot click handlers
    dots?.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        goToSlide(index);
        startAutoRotate();
      });
    });

    // Pause on hover
    carousel.addEventListener('mouseenter', stopAutoRotate);
    carousel.addEventListener('mouseleave', startAutoRotate);

    // Start auto-rotation
    startAutoRotate();
  }

  private showLevelModal(level: Level): void {
    // Level availability uses start_date + level_timeline only.
    // end_date on Level is intentionally ignored — level_timeline is the
    // level's end marker.
    const now = new Date();

    if (level.start_date) {
      const startDate = this.parseLevelDate(level.start_date);
      if (startDate && startDate > now) {
        this.showUnavailableDialog(this.t.levelUnavailableTitle, this.t.levelNotStarted);
        return;
      }
    }

    if (level.level_timeline !== undefined && level.level_timeline !== null) {
      const timeline = this.parseLevelDate(level.level_timeline);
      if (timeline && timeline < now) {
        this.showUnavailableDialog(this.t.levelUnavailableTitle, this.t.levelEnded);
        return;
      }
    }

    let inputHtml = '';

    switch (level.level_type) {
      case 'text':
        inputHtml = `
          <div class="luqta-input-group">
            <label class="luqta-input-label">${this.t.enterAnswer}</label>
            <input type="text" class="luqta-input" id="luqta-level-input" placeholder="${this.t.enterAnswer}">
          </div>
        `;
        break;
      case 'qr':
        inputHtml = `
          <div class="luqta-level-modal-content">
            <span class="luqta-level-type-label">${this.t.scanQR}</span>
            <h3 class="luqta-level-modal-title">${this.t.scanQRDescription || 'Please scan a code to verify that you\'ve complete the level.'}</h3>

            <div class="luqta-qr-scanner-area">
              <div class="luqta-qr-scanner-preview" id="luqta-qr-preview">
                <video id="luqta-qr-video" autoplay playsinline></video>
              </div>
              <div class="luqta-qr-icon-placeholder" id="luqta-qr-placeholder">
                ${ICONS.qr}
              </div>
            </div>

            <button class="luqta-level-action-btn" id="luqta-start-camera">
              ${this.t.scanNow || 'Scan Now'}
            </button>
            <div class="luqta-qr-status" id="luqta-qr-status"></div>
          </div>
        `;
        break;
      case 'link':
        inputHtml = `
          <div class="luqta-input-group">
            <p style="margin-bottom: 16px; color: var(--luqta-text-muted);">
              ${level.link ? `Visit: <a href="${level.link}" target="_blank" style="color: var(--luqta-primary);">${level.link}</a>` : this.t.visitLink}
            </p>
            <button class="luqta-btn luqta-btn-secondary luqta-btn-block" id="luqta-visit-link">
              ${ICONS.link} ${this.t.visitLink}
            </button>
          </div>
        `;
        break;
      case 'image':
        inputHtml = `
          <div class="luqta-level-modal-content">
            <span class="luqta-level-type-label">${this.t.uploadPhoto}</span>
            <h3 class="luqta-level-modal-title">${this.t.uploadPhotoDescription}</h3>

            <div class="luqta-upload-area" id="luqta-upload-area">
              <input type="file" class="luqta-upload-input" id="luqta-level-input" accept="image/jpeg,image/png" style="display: none;">
              <div class="luqta-upload-placeholder" id="luqta-upload-placeholder">
                <div class="luqta-upload-icon">${ICONS.image}</div>
                <p class="luqta-upload-hint">${this.t.supportedFormats}<br>${this.t.maxPhotoSize}</p>
              </div>
              <div class="luqta-upload-preview" id="luqta-upload-preview" style="display: none;">
                <img id="luqta-preview-image" src="" alt="Preview">
              </div>
            </div>

            <button class="luqta-level-action-btn" id="luqta-upload-btn">
              ${this.t.uploadPhotoBtn} ${ICONS.camera}
            </button>
          </div>
        `;
        break;
      case 'quiz':
        inputHtml = `
          <div class="luqta-input-group">
            <button class="luqta-btn luqta-btn-secondary luqta-btn-block" id="luqta-start-quiz">
              ${ICONS.quiz} ${this.t.takeQuiz}
            </button>
          </div>
        `;
        break;
    }

    const levelName = this.config.locale === 'ar' && level.arabic_name ? level.arabic_name : level.name;
    const levelDesc = this.config.locale === 'ar' && level.arabic_description ? level.arabic_description : level.description;

    // Use special compact modal for QR and image types
    const isSpecialModal = level.level_type === 'qr' || level.level_type === 'image';

    const modalHtml = isSpecialModal ? `
      <div class="luqta-modal-overlay" id="luqta-modal">
        <div class="luqta-modal luqta-level-modal-special">
          <button class="luqta-modal-close-float" id="luqta-modal-close">${ICONS.close}</button>
          ${inputHtml}
        </div>
      </div>
    ` : `
      <div class="luqta-modal-overlay" id="luqta-modal">
        <div class="luqta-modal">
          <div class="luqta-modal-header">
            <h3 class="luqta-modal-title">${levelName}</h3>
            <button class="luqta-modal-close" id="luqta-modal-close">${ICONS.close}</button>
          </div>
          <div class="luqta-modal-body">
            <p style="margin-bottom: 20px; color: var(--luqta-text-muted);">${levelDesc}</p>
            ${inputHtml}
          </div>
          <div class="luqta-modal-footer">
            <button class="luqta-btn luqta-btn-outline" id="luqta-cancel">${this.t.cancel}</button>
            ${level.level_type !== 'quiz' && level.level_type !== 'link' ? `
              <button class="luqta-btn luqta-btn-primary" id="luqta-submit-level">
                ${this.t.submitAnswer}
              </button>
            ` : ''}
          </div>
        </div>
      </div>
    `;

    this.container.insertAdjacentHTML('beforeend', modalHtml);
    this.setupLevelModalHandlers(level);
  }

  private showCongratulationModal(
    type: 'level' | 'contest',
    congratsData: {
      points_earned: number;
      time_spent_in_level?: string;
      share_points?: number;
      next_level_id?: number;
      is_contest_completed?: boolean;
      level_name?: string;
      contest_name?: string;
      level_status?: string;
    }
  ): void {
    const isContest = type === 'contest' || congratsData.is_contest_completed;
    const hasNextLevel = !isContest && congratsData.next_level_id;

    // Format time display
    const timeDisplay = congratsData.time_spent_in_level || '00:00:00';

    const modalHtml = `
      <div class="luqta-modal-overlay" id="luqta-modal">
        <div class="luqta-modal luqta-congrats-modal-v2">
          <div class="luqta-congrats-content">
            <!-- Confetti decorations -->
            <div class="luqta-confetti-wrap">
              <span class="luqta-confetti-piece" style="--x: -60px; --y: -40px; --r: 45deg; --c: #10b981;"></span>
              <span class="luqta-confetti-piece" style="--x: 70px; --y: -30px; --r: -30deg; --c: #f59e0b;"></span>
              <span class="luqta-confetti-piece" style="--x: -80px; --y: 20px; --r: 20deg; --c: #ec4899;"></span>
              <span class="luqta-confetti-piece" style="--x: 90px; --y: 10px; --r: -60deg; --c: #6366f1;"></span>
              <span class="luqta-confetti-piece luqta-confetti-spiral" style="--x: -50px; --y: -60px; --c: #84cc16;"></span>
              <span class="luqta-confetti-piece luqta-confetti-spiral" style="--x: 60px; --y: -50px; --c: #f97316;"></span>
              <span class="luqta-confetti-dot" style="--x: -70px; --y: 40px; --c: #14b8a6;"></span>
              <span class="luqta-confetti-dot" style="--x: 80px; --y: 50px; --c: #a855f7;"></span>
              <span class="luqta-confetti-dot" style="--x: -40px; --y: 60px; --c: #eab308;"></span>
              <span class="luqta-confetti-dot" style="--x: 50px; --y: 70px; --c: #ef4444;"></span>
            </div>

            <!-- Badge SVG Icon -->
            <div class="luqta-badge-container">
              ${ICONS.badge}
            </div>

            <!-- Congratulations Title -->
            <h2 class="luqta-congrats-title-v2">${this.t.congratulations}</h2>

            <!-- Level/Contest Name -->
            ${congratsData.level_name ? `
              <p class="luqta-congrats-level-name">${congratsData.level_name}</p>
            ` : ''}

            <!-- Points Earned -->
            <p class="luqta-congrats-points-text-v2">
              ${this.t.youEarned} <span class="luqta-points-value">+${congratsData.points_earned} ${this.t.points}!</span>
            </p>

            <!-- Luqta Logo -->
            <div class="luqta-congrats-brand">
              <img src="https://luqta.io/logo.png" alt="Luqta" onerror="this.parentElement.innerHTML='<span class=\\'luqta-brand-text\\'>Luqta</span>'">
            </div>

            <!-- Time Section -->
            <div class="luqta-time-box">
              <div class="luqta-time-icon-wrap">
                ${ICONS.clock}
              </div>
              <span class="luqta-time-label-text">${this.t.youFinishedIn}</span>
              <div class="luqta-time-value-display">${timeDisplay}</div>
            </div>

            <!-- Action Buttons -->
            <div class="luqta-congrats-btns">
              ${congratsData.share_points ? `
                <button class="luqta-share-btn-v2" id="luqta-share-congrats">
                  ${this.t.shareAndEarn} (${congratsData.share_points}) ${this.t.points}
                </button>
              ` : ''}

              <button class="luqta-next-btn-v2" id="${hasNextLevel ? 'luqta-next-level' : 'luqta-back-contest'}">
                ${hasNextLevel ? this.t.goToNextLevel : this.t.backToContest}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    this.container.insertAdjacentHTML('beforeend', modalHtml);

    // Share button handler
    document.getElementById('luqta-share-congrats')?.addEventListener('click', () => {
      const contest = this.state.selectedContest;
      if (navigator.share && contest) {
        navigator.share({
          title: contest.name,
          text: `I just earned ${congratsData.points_earned} points!`,
          url: window.location.href
        }).catch(() => { });
      }
    });

    // Next level button handler
    document.getElementById('luqta-next-level')?.addEventListener('click', () => {
      this.closeModal();
      // Find and open next level
      if (congratsData.next_level_id && this.state.selectedContest?.levels) {
        const nextLevel = this.state.selectedContest.levels.find(l => l.id === congratsData.next_level_id);
        if (nextLevel) {
          this.refreshContestDetail().then(() => {
            this.showLevelModal(nextLevel);
          });
          return;
        }
      }
      this.refreshContestDetail();
    });

    // Back to contest button handler
    document.getElementById('luqta-back-contest')?.addEventListener('click', () => {
      this.closeModal();

      if (isContest) {
        // Go back to contest list
        this.state.currentView = 'contests';
        this.state.selectedContest = null;
        this.loadContests();
      } else if (this.state.selectedContest) {
        // Refresh contest detail
        this.refreshContestDetail();
      }
    });
  }

  /**
   * Shows contest completion modal with full contest data from compete API
   */
  private showContestCompletionModal(data: {
    contest_id: number;
    contest_name: string;
    arabic_name?: string;
    status: string;
    progress: {
      total_levels: number;
      completed_levels: number;
      completion_percentage: number;
      points_earned: number;
      time_spent_ms: number;
    };
    levels?: Array<{
      id: number;
      name: string;
      arabic_name?: string;
      level_type: string;
      points: number;
      isCompleted: boolean;
    }>;
    prizes?: Array<{
      id: number;
      name: string;
      description?: string;
      value: number;
      image?: { url: string };
    }>;
    draw_date?: number;
  }): void {
    const contestName = this.config.locale === 'ar' && data.arabic_name ? data.arabic_name : data.contest_name;

    // Format time spent
    const timeSpentMs = data.progress.time_spent_ms || 0;
    const hours = Math.floor(timeSpentMs / 3600000);
    const minutes = Math.floor((timeSpentMs % 3600000) / 60000);
    const seconds = Math.floor((timeSpentMs % 60000) / 1000);
    const timeSpent = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    // Format draw date
    let drawDateStr = '';
    if (data.draw_date) {
      const drawDate = new Date(data.draw_date);
      drawDateStr = drawDate.toLocaleDateString(this.config.locale === 'ar' ? 'ar-SA' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    // Build prizes HTML
    let prizesHtml = '';
    if (data.prizes && data.prizes.length > 0) {
      prizesHtml = `
        <div class="luqta-contest-complete-prizes">
          <h4 class="luqta-contest-complete-prizes-title">${this.t.viewPrizes}</h4>
          <div class="luqta-contest-complete-prizes-list">
            ${data.prizes.map(prize => `
              <div class="luqta-contest-complete-prize">
                ${prize.image?.url ? `<img src="${prize.image.url}" alt="${prize.name}">` : `<div class="luqta-prize-placeholder">${ICONS.gift}</div>`}
                <span>${prize.name}</span>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    const modalHtml = `
      <div class="luqta-modal-overlay" id="luqta-modal">
        <div class="luqta-modal luqta-contest-complete-modal">
          <div class="luqta-contest-complete-content">
            <!-- Confetti decorations -->
            <div class="luqta-confetti-wrap">
              <span class="luqta-confetti-piece" style="--x: -60px; --y: -40px; --r: 45deg; --c: #10b981;"></span>
              <span class="luqta-confetti-piece" style="--x: 70px; --y: -30px; --r: -30deg; --c: #f59e0b;"></span>
              <span class="luqta-confetti-piece" style="--x: -80px; --y: 20px; --r: 20deg; --c: #ec4899;"></span>
              <span class="luqta-confetti-piece" style="--x: 90px; --y: 10px; --r: -60deg; --c: #6366f1;"></span>
              <span class="luqta-confetti-dot" style="--x: -70px; --y: 40px; --c: #14b8a6;"></span>
              <span class="luqta-confetti-dot" style="--x: 80px; --y: 50px; --c: #a855f7;"></span>
            </div>

            <!-- Trophy Icon -->
            <div class="luqta-contest-complete-icon">
              ${ICONS.trophy}
            </div>

            <!-- Title -->
            <h2 class="luqta-contest-complete-title">${this.t.congratulations}</h2>
            <p class="luqta-contest-complete-subtitle">${this.t.congratulationsContest}</p>
            <p class="luqta-contest-complete-name">${contestName}</p>

            <!-- Stats Grid -->
            <div class="luqta-contest-complete-stats">
              <div class="luqta-contest-complete-stat">
                <div class="luqta-contest-complete-stat-value">${data.progress.points_earned}</div>
                <div class="luqta-contest-complete-stat-label">${this.t.totalPointsEarned}</div>
              </div>
              <div class="luqta-contest-complete-stat">
                <div class="luqta-contest-complete-stat-value">${data.progress.completed_levels}/${data.progress.total_levels}</div>
                <div class="luqta-contest-complete-stat-label">${this.t.levelsCompleted}</div>
              </div>
              <div class="luqta-contest-complete-stat">
                <div class="luqta-contest-complete-stat-value">${timeSpent}</div>
                <div class="luqta-contest-complete-stat-label">${this.t.timeSpent}</div>
              </div>
            </div>

            ${drawDateStr ? `
              <div class="luqta-contest-complete-draw">
                ${ICONS.calendar}
                <span>${this.t.drawDate}: ${drawDateStr}</span>
              </div>
            ` : ''}

            ${prizesHtml}

            <!-- Action Button -->
            <div class="luqta-contest-complete-actions">
              <button class="luqta-btn luqta-btn-primary luqta-btn-block" id="luqta-back-to-contests">
                ${this.t.backToContests}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    this.container.insertAdjacentHTML('beforeend', modalHtml);

    // Setup handler
    document.getElementById('luqta-back-to-contests')?.addEventListener('click', () => {
      this.closeModal();
      this.state.currentView = 'contests';
      this.state.selectedContest = null;
      this.loadContests();
    });
  }

  private closeModal(): void {
    const modal = document.getElementById('luqta-modal');
    if (modal) {
      modal.remove();
    }
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  private async onContestClick(contest: Contest): Promise<void> {
    // Track contest click (fire-and-forget)
    this.client.applicationRequest(USER_ENDPOINTS.ON_CONTEST_CLICK, {
      method: 'POST',
      body: { contest_id: contest.id },
    }).catch(() => {});

    // Check if user is initialized
    if (!this.client.isInitialized()) {
      try {
        await this.client.initializeUser();
      } catch (error) {
        if (this.config.onError && error instanceof LuqtaError) {
          this.config.onError(error);
        }
        this.showToast('error', 'Please sign in to participate');
        return;
      }
    }

    // Try to get contest details with progress
    try {
      this.showLoadingOverlay();
      const response = await this.client.getContestDetailsProgress(contest.id);
      this.hideLoadingOverlay();

      if (response.success && response.data) {
        // User is participating, show contest detail with progress
        contest.participate_progress = response.data.participate_progress || {
          status: 'in_progress',
          participant_id: 0
        };
        this.renderContestDetail(contest, response.data);
      }
    } catch (error: any) {
      this.hideLoadingOverlay();

      // Check if error is "Please Participate This Contest"
      if (error?.code === 'ERROR_FATAL' || error?.message?.includes('Participate')) {
        // Show participation popup
        this.showParticipationRequiredModal(contest);
      } else {
        if (this.config.onError && error instanceof LuqtaError) {
          this.config.onError(error);
        }
        this.showToast('error', error?.message || 'Failed to load contest details');
      }
    }
  }

  /**
   * Shows a loading overlay while fetching data
   */
  private showLoadingOverlay(): void {
    const overlay = document.createElement('div');
    overlay.id = 'luqta-loading-overlay';
    overlay.innerHTML = `
      <div class="luqta-loading-spinner">
        <div class="luqta-spinner"></div>
        <p>${this.t.loading}</p>
      </div>
    `;
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10001;
    `;
    document.body.appendChild(overlay);
  }

  /**
   * Hides the loading overlay
   */
  private hideLoadingOverlay(): void {
    document.getElementById('luqta-loading-overlay')?.remove();
  }

  /**
   * Shows participation required modal when user hasn't joined the contest
   */
  private showParticipationRequiredModal(contest: Contest): void {
    const isPrivate = contest.type === 'private';
    const contestTitle = this.config.locale === 'ar' && contest.arabic_name ? contest.arabic_name : contest.name;

    const modalHtml = `
      <div class="luqta-modal-overlay" id="luqta-modal">
        <div class="luqta-modal">
          <div class="luqta-modal-header">
            <h2>${this.t.participationRequired}</h2>
            <button class="luqta-modal-close" id="luqta-modal-close">${ICONS.close}</button>
          </div>
          <div class="luqta-modal-body">
            <div class="luqta-participation-content">
              <div class="luqta-participation-icon">${ICONS.trophy}</div>
              <h3>${contestTitle}</h3>
              <p>${this.t.participationRequiredDesc}</p>
              ${isPrivate ? `
                <div class="luqta-access-code-section">
                  <label class="luqta-private-badge">${ICONS.lock} ${this.t.privateContest}</label>
                  <input
                    type="text"
                    id="luqta-access-code"
                    class="luqta-input"
                    placeholder="${this.t.accessCodePlaceholder}"
                  />
                </div>
              ` : ''}
            </div>
          </div>
          <div class="luqta-modal-footer">
            <button class="luqta-btn luqta-btn-secondary" id="luqta-cancel">${this.t.cancel}</button>
            <button class="luqta-btn luqta-btn-primary" id="luqta-join-contest">${this.t.participate}</button>
          </div>
        </div>
      </div>
    `;

    this.container.insertAdjacentHTML('beforeend', modalHtml);
    this.setupParticipationModalHandlers(contest);
  }

  /**
   * Sets up handlers for the participation required modal
   */
  private setupParticipationModalHandlers(contest: Contest): void {
    const closeBtn = document.getElementById('luqta-modal-close');
    const cancelBtn = document.getElementById('luqta-cancel');
    const joinBtn = document.getElementById('luqta-join-contest');
    const accessCodeInput = document.getElementById('luqta-access-code') as HTMLInputElement | null;

    const close = () => this.closeModal();

    closeBtn?.addEventListener('click', close);
    cancelBtn?.addEventListener('click', close);

    joinBtn?.addEventListener('click', async () => {
      const accessCode = accessCodeInput?.value?.trim() || undefined;

      // Contest availability: block join when start_date is in the future
      // or end_date has already passed.
      const now = new Date();
      if (contest.start_date) {
        const contestStart = this.parseLevelDate(contest.start_date);
        if (contestStart && contestStart > now) {
          this.closeModal();
          this.showUnavailableDialog(this.t.contestUnavailableTitle, this.t.contestNotStarted);
          return;
        }
      }
      if (contest.end_date) {
        const contestEnd = this.parseLevelDate(contest.end_date);
        if (contestEnd && contestEnd < now) {
          this.closeModal();
          this.showUnavailableDialog(this.t.contestUnavailableTitle, this.t.contestEndedMessage);
          return;
        }
      }

      // For private contests, require access code
      if (contest.type === 'private' && !accessCode) {
        this.showToast('error', this.t.enterAccessCode);
        accessCodeInput?.focus();
        return;
      }

      joinBtn.textContent = this.t.joining;
      joinBtn.setAttribute('disabled', 'true');

      try {
        await this.client.participateContest(contest.id, accessCode);

        // Update contest in state
        contest.participate_progress = {
          participant_id: 0,
          status: 'in_progress',
        };

        this.closeModal();
        this.showToast('success', this.t.joinSuccess);

        // Trigger action callback
        if (this.config.onAction) {
          this.config.onAction({
            type: 'contest_joined',
            data: { contest_id: contest.id, contest_name: contest.name }
          });
        }

        // Now fetch contest details and show detail page
        try {
          const response = await this.client.getContestDetailsProgress(contest.id);
          if (response.success && response.data) {
            this.renderContestDetail(contest, response.data);
          }
        } catch (detailError) {
          // If details fail, just show the contest detail without extra data
          this.renderContestDetail(contest);
        }
      } catch (error: any) {
        joinBtn.textContent = this.t.participate;
        joinBtn.removeAttribute('disabled');

        if (this.config.onError && error instanceof LuqtaError) {
          this.config.onError(error);
        }
        this.showToast('error', error?.message || 'Failed to join contest');
      }
    });
  }

  private setupModalHandlers(contest: Contest): void {
    const closeBtn = document.getElementById('luqta-modal-close');
    const cancelBtn = document.getElementById('luqta-cancel');
    const participateBtn = document.getElementById('luqta-participate');

    const close = () => this.closeModal();

    closeBtn?.addEventListener('click', close);
    cancelBtn?.addEventListener('click', close);

    participateBtn?.addEventListener('click', async () => {
      // Contest availability: block join when start_date is in the future
      // or end_date has already passed.
      const now = new Date();
      if (contest.start_date) {
        const contestStart = this.parseLevelDate(contest.start_date);
        if (contestStart && contestStart > now) {
          this.closeModal();
          this.showUnavailableDialog(this.t.contestUnavailableTitle, this.t.contestNotStarted);
          return;
        }
      }
      if (contest.end_date) {
        const contestEnd = this.parseLevelDate(contest.end_date);
        if (contestEnd && contestEnd < now) {
          this.closeModal();
          this.showUnavailableDialog(this.t.contestUnavailableTitle, this.t.contestEndedMessage);
          return;
        }
      }

      participateBtn.textContent = this.t.participating;
      participateBtn.setAttribute('disabled', 'true');

      try {
        await this.client.contests.participate(contest.id);

        // Update contest in state
        contest.participate_progress = {
          participant_id: 0,
          status: 'in_progress',
        };

        this.closeModal();
        this.renderContestDetail(contest);

        // Trigger action callback
        if (this.config.onAction) {
          this.config.onAction({
            type: 'contest_joined',
            data: { contest_id: contest.id, contest_name: contest.name }
          });
        }
      } catch (error) {
        participateBtn.textContent = this.t.participate;
        participateBtn.removeAttribute('disabled');

        if (this.config.onError && error instanceof LuqtaError) {
          this.config.onError(error);
        }
        this.showToast('error', error instanceof Error ? error.message : 'Failed to join contest');
      }
    });
  }

  // Track active camera stream for cleanup
  private activeCameraStream: MediaStream | null = null;

  private setupLevelModalHandlers(level: Level): void {
    const closeBtn = document.getElementById('luqta-modal-close');
    const cancelBtn = document.getElementById('luqta-cancel');
    const submitBtn = document.getElementById('luqta-submit-level');
    const visitLinkBtn = document.getElementById('luqta-visit-link');
    const startQuizBtn = document.getElementById('luqta-start-quiz');
    const startCameraBtn = document.getElementById('luqta-start-camera');

    const close = () => {
      this.stopCameraStream();
      this.closeModal();
    };

    closeBtn?.addEventListener('click', close);
    cancelBtn?.addEventListener('click', close);

    // Visit link handler
    if (visitLinkBtn && level.link) {
      visitLinkBtn.addEventListener('click', async () => {
        window.open(level.link, '_blank');

        try {
          await this.client.levels.complete(level.id, { link: level.link });
          this.closeModal();
          await this.onLevelCompleted(level);
        } catch (error) {
          this.showToast('error', 'Failed to complete level');
        }
      });
    }

    // Start quiz handler
    if (startQuizBtn && level.quizzes_id) {
      startQuizBtn.addEventListener('click', async () => {
        startQuizBtn.textContent = this.t.loading;
        startQuizBtn.setAttribute('disabled', 'true');

        try {
          const response = await this.client.quiz.start(level.quizzes_id!, level.id);
          this.closeModal();

          if (response.data) {
            this.showQuizModal(level, response.data);
          }
        } catch (error) {
          startQuizBtn.textContent = `${ICONS.quiz} ${this.t.takeQuiz}`;
          startQuizBtn.removeAttribute('disabled');
          this.showToast('error', error instanceof Error ? error.message : 'Failed to start quiz');
        }
      });
    }

    // Submit handler for text/qr/image
    if (submitBtn) {
      submitBtn.addEventListener('click', async () => {
        const input = document.getElementById('luqta-level-input') as HTMLInputElement;
        if (!input) return;

        submitBtn.textContent = this.t.submitting;
        submitBtn.setAttribute('disabled', 'true');

        try {
          if (level.level_type === 'image') {
            const file = input.files?.[0];
            if (!file) {
              this.showToast('error', 'Please select an image');
              submitBtn.textContent = this.t.submitAnswer;
              submitBtn.removeAttribute('disabled');
              return;
            }

            const base64 = await this.fileToBase64(file);
            await this.client.levels.completeWithImage(level.id, base64);
          } else {
            const data: any = {};
            if (level.level_type === 'text') {
              data.textContent = input.value;
            } else if (level.level_type === 'qr') {
              data.qrCode = input.value;
            }

            await this.client.levels.complete(level.id, data);
          }

          this.closeModal();
          await this.onLevelCompleted(level);
        } catch (error) {
          submitBtn.textContent = this.t.submitAnswer;
          submitBtn.removeAttribute('disabled');
          this.showToast('error', error instanceof Error ? error.message : 'Failed to complete level');
        }
      });
    }

    // QR Camera handler
    if (startCameraBtn && level.level_type === 'qr') {
      startCameraBtn.addEventListener('click', () => this.startQRScanner(level));
    }

    // Image upload handlers
    if (level.level_type === 'image') {
      const uploadBtn = document.getElementById('luqta-upload-btn');
      const uploadPlaceholder = document.getElementById('luqta-upload-placeholder');
      const fileInput = document.getElementById('luqta-level-input') as HTMLInputElement;
      const previewContainer = document.getElementById('luqta-upload-preview');
      const previewImage = document.getElementById('luqta-preview-image') as HTMLImageElement;

      // Click on upload button or placeholder to trigger file input
      const triggerFileInput = () => fileInput?.click();

      uploadBtn?.addEventListener('click', triggerFileInput);
      uploadPlaceholder?.addEventListener('click', triggerFileInput);

      // Handle file selection
      fileInput?.addEventListener('change', async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;

        // Validate file size (2MB max)
        if (file.size > 2 * 1024 * 1024) {
          this.showToast('error', this.t.maxPhotoSize);
          fileInput.value = '';
          return;
        }

        // Validate file type
        if (!['image/jpeg', 'image/png'].includes(file.type)) {
          this.showToast('error', this.t.supportedFormats);
          fileInput.value = '';
          return;
        }

        // Show preview
        const reader = new FileReader();
        reader.onload = (event) => {
          if (previewImage && previewContainer && uploadPlaceholder) {
            previewImage.src = event.target?.result as string;
            previewContainer.style.display = 'block';
            uploadPlaceholder.style.display = 'none';
          }
        };
        reader.readAsDataURL(file);

        // Auto-submit after selecting image
        if (uploadBtn) {
          uploadBtn.textContent = this.t.submitting;
          uploadBtn.setAttribute('disabled', 'true');
        }

        try {
          const base64 = await this.fileToBase64(file);
          await this.client.levels.completeWithImage(level.id, base64);
          this.closeModal();
          await this.onLevelCompleted(level);
        } catch (error) {
          if (uploadBtn) {
            uploadBtn.innerHTML = `${this.t.uploadPhotoBtn} ${ICONS.camera}`;
            uploadBtn.removeAttribute('disabled');
          }
          // Reset preview
          if (previewContainer && uploadPlaceholder) {
            previewContainer.style.display = 'none';
            uploadPlaceholder.style.display = 'flex';
          }
          this.showToast('error', error instanceof Error ? error.message : 'Failed to upload image');
        }
      });
    }
  }

  /**
   * Starts the QR code scanner using device camera
   */
  private async startQRScanner(level: Level): Promise<void> {
    const video = document.getElementById('luqta-qr-video') as HTMLVideoElement;
    const preview = document.getElementById('luqta-qr-preview');
    const startBtn = document.getElementById('luqta-start-camera');
    const statusEl = document.getElementById('luqta-qr-status');

    if (!video || !preview) return;

    try {
      // Request camera permission
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' } // Prefer back camera
      });

      this.activeCameraStream = stream;
      video.srcObject = stream;
      preview.classList.add('active');

      if (startBtn) {
        startBtn.style.display = 'none';
      }

      if (statusEl) {
        statusEl.textContent = this.t.scanningQR;
        statusEl.className = 'luqta-qr-status scanning';
      }

      // Start scanning for QR codes
      this.scanQRFromVideo(video, level);

    } catch (error) {
      console.error('Camera error:', error);
      if (statusEl) {
        statusEl.textContent = this.t.cameraPermissionDenied;
        statusEl.className = 'luqta-qr-status error';
      }
      this.showToast('error', this.t.cameraPermissionDenied);
    }
  }

  /**
   * Scans QR code from video stream
   * Uses canvas to capture frames and check for QR patterns
   */
  private scanQRFromVideo(video: HTMLVideoElement, level: Level): void {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const expectedQRData = level.qrData || level.textContent || '';

    const scan = () => {
      if (!this.activeCameraStream || video.paused || video.ended) return;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0);

      // Try to detect QR code using BarcodeDetector API if available
      if ('BarcodeDetector' in window) {
        const barcodeDetector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
        barcodeDetector.detect(canvas)
          .then((barcodes: any[]) => {
            if (barcodes.length > 0) {
              const detectedData = barcodes[0].rawValue;
              this.handleQRDetected(detectedData, expectedQRData, level);
            } else {
              // Continue scanning
              requestAnimationFrame(scan);
            }
          })
          .catch(() => {
            requestAnimationFrame(scan);
          });
      } else {
        // Fallback: If BarcodeDetector not available, use simple pattern matching
        // For now, we'll show a manual entry option
        const statusEl = document.getElementById('luqta-qr-status');
        if (statusEl && !statusEl.querySelector('input')) {
          statusEl.innerHTML = `
            <p style="margin-bottom: 8px; font-size: 12px; color: var(--luqta-text-muted);">
              Point your camera at the QR code. If scanning doesn't work, enter the code manually:
            </p>
            <input type="text" class="luqta-input" id="luqta-qr-manual-input" placeholder="Enter QR code data" style="margin-bottom: 8px;">
            <button class="luqta-btn luqta-btn-primary luqta-btn-sm" id="luqta-qr-manual-submit">Submit</button>
          `;

          document.getElementById('luqta-qr-manual-submit')?.addEventListener('click', () => {
            const manualInput = document.getElementById('luqta-qr-manual-input') as HTMLInputElement;
            if (manualInput?.value) {
              this.handleQRDetected(manualInput.value, expectedQRData, level);
            }
          });
        }
        // Continue scanning anyway in case BarcodeDetector becomes available
        requestAnimationFrame(scan);
      }
    };

    // Start scanning when video is ready
    video.onloadedmetadata = () => {
      video.play();
      scan();
    };
  }

  /**
   * Handles detected QR code data
   */
  private async handleQRDetected(detectedData: string, expectedData: string, level: Level): Promise<void> {
    const statusEl = document.getElementById('luqta-qr-status');

    // Check if detected QR matches expected
    if (detectedData === expectedData || !expectedData) {
      if (statusEl) {
        statusEl.textContent = this.t.qrCodeDetected;
        statusEl.className = 'luqta-qr-status success';
      }

      this.stopCameraStream();

      try {
        await this.client.levels.complete(level.id, { qrCode: detectedData });
        this.closeModal();
        await this.onLevelCompleted(level);
      } catch (error) {
        this.showToast('error', error instanceof Error ? error.message : 'Failed to complete level');
      }
    } else {
      if (statusEl) {
        statusEl.textContent = 'Invalid QR code. Please scan the correct QR code.';
        statusEl.className = 'luqta-qr-status error';
      }
      // Continue scanning
      const video = document.getElementById('luqta-qr-video') as HTMLVideoElement;
      if (video) {
        setTimeout(() => this.scanQRFromVideo(video, level), 1000);
      }
    }
  }

  /**
   * Stops the camera stream
   */
  private stopCameraStream(): void {
    if (this.activeCameraStream) {
      this.activeCameraStream.getTracks().forEach(track => track.stop());
      this.activeCameraStream = null;
    }
  }

  private async onLevelCompleted(level: Level): Promise<void> {
    // Check if all levels completed
    const contest = this.state.selectedContest;
    if (!contest || !contest.levels) return;

    const completedLevels = contest.levels.filter(l => this.isLevelCompleted(l) || l.id === level.id);
    const allCompleted = completedLevels.length === contest.levels.length;

    // Trigger action callback
    if (this.config.onAction) {
      this.config.onAction({
        type: 'level_completed',
        data: {
          level_id: level.id,
          level_name: level.name,
          points_earned: level.points
        }
      });
    }

    // Fetch congratulation data from API
    let congratsData: {
      points_earned: number;
      time_spent_in_level?: string;
      share_points?: number;
      next_level_id?: number;
      is_contest_completed?: boolean;
      level_name?: string;
      contest_name?: string;
      level_status?: string;
    } = {
      points_earned: level.points,
      is_contest_completed: allCompleted,
      level_name: level.name
    };

    try {
      const response = await this.client.levels.getCongratulation(level.id, contest.id);
      // API response is wrapped: { success, data: { success, data: {...} } }
      // So actual data is at response.data.data or response.data (depending on API structure)
      const apiData = response.data?.data || response.data;
      if (apiData) {
        // Map API response fields correctly
        congratsData = {
          points_earned: apiData.points_earned || level.points,
          time_spent_in_level: apiData.time_spent_in_level,
          share_points: apiData.share_points,
          next_level_id: apiData.next_level_id,
          is_contest_completed: apiData.level_status === 'Complete' && allCompleted,
          level_name: apiData.level_name || level.name,
          contest_name: apiData.contest_name || contest.name,
          level_status: apiData.level_status
        };
      }
    } catch (error) {
      // Use fallback data if API fails
      console.warn('Failed to fetch congratulation data:', error);
    }

    if (allCompleted || congratsData.is_contest_completed) {
      // Mark contest as completed
      contest.participate_progress = {
        ...contest.participate_progress!,
        status: 'completed'
      };

      // Call compete API to get contest completion data
      try {
        const competeResponse = await this.client.contests.compete(contest.id);
        const competeData = competeResponse.data?.data || competeResponse.data;

        if (competeData && competeData.show_congratulations) {
          // Show contest completion modal with full data
          this.showContestCompletionModal(competeData);
        } else {
          // Fallback to simple congratulation modal
          this.showCongratulationModal('contest', congratsData);
        }
      } catch (error) {
        console.warn('Failed to fetch compete data:', error);
        // Fallback to simple congratulation modal
        this.showCongratulationModal('contest', congratsData);
      }

      if (this.config.onAction) {
        this.config.onAction({
          type: 'contest_joined',
          data: {
            contest_id: contest.id,
            contest_name: contest.name,
            total_points: contest.total_points,
            status: 'completed'
          }
        });
      }
    } else {
      // Find next level
      const currentLevelIndex = contest.levels.findIndex(l => l.id === level.id);
      if (currentLevelIndex >= 0 && currentLevelIndex < contest.levels.length - 1) {
        congratsData.next_level_id = contest.levels[currentLevelIndex + 1].id;
      }

      // Show level completion popup
      this.showCongratulationModal('level', congratsData);
    }
  }

  private async refreshContestDetail(): Promise<void> {
    if (!this.state.selectedContest) return;

    try {
      const response = await this.client.contests.getProgress(this.state.selectedContest.id);
      if (response.data) {
        // Update contest with fresh data
        Object.assign(this.state.selectedContest, response.data);
      }
      this.renderContestDetail(this.state.selectedContest);
    } catch (error) {
      // Just re-render with current data
      this.renderContestDetail(this.state.selectedContest);
    }
  }

  // ============================================================================
  // QUIZ FLOW
  // ============================================================================

  private currentQuizState: {
    level: Level;
    levelId: number;
    attemptId: number;
    quiz: Quiz;
    progress: QuizProgress;
    currentQuestion: QuizQuestion;
    isSubmitting: boolean;
    feedbackMessage: string | null;
    selectedOptionId: number | null;
  } | null = null;

  private showQuizModal(level: Level, quizData: {
    attempt: QuizAttempt;
    quiz: Quiz;
    progress: QuizProgress;
    current_question: QuizQuestion;
  }): void {
    // Store quiz state for progressive flow
    this.currentQuizState = {
      level,
      levelId: level.id,
      attemptId: quizData.attempt.id,
      quiz: quizData.quiz,
      progress: quizData.progress,
      currentQuestion: quizData.current_question,
      isSubmitting: false,
      feedbackMessage: null,
      selectedOptionId: null,
    };

    this.renderQuizQuestion();
  }

  private renderQuizQuestion(): void {
    if (!this.currentQuizState) return;

    const { quiz, progress, currentQuestion, isSubmitting, feedbackMessage, selectedOptionId } = this.currentQuizState;
    const questionNumber = progress.current_question_number || (progress.current_question_index + 1);
    const totalQuestions = progress.total_questions;
    const hasSelection = selectedOptionId !== null;
    const answerBtnLabel = `${this.t.quizAnswerBtn} (${questionNumber} ${this.t.of} ${totalQuestions})`;

    const modalHtml = `
      <div class="luqta-modal-overlay" id="luqta-modal">
        <div class="luqta-modal luqta-quiz-modal">
          <div class="luqta-quiz-body">
            ${quiz.has_time_tracking && quiz.max_time_seconds ? `
              <span class="luqta-quiz-timer" id="luqta-quiz-timer">
                ${ICONS.clock} <span id="luqta-timer-value">${this.formatTime(quiz.max_time_seconds)}</span>
              </span>
            ` : ''}
            <div class="luqta-quiz-instruction">${this.t.quizInstruction}</div>
            <h4 class="luqta-question-text">${currentQuestion.question}</h4>
            <div class="luqta-quiz-options" id="luqta-quiz-options">
              ${currentQuestion.options.map(option => {
                const isSelected = selectedOptionId === option.id;
                return `
                  <button class="luqta-quiz-option${isSelected ? ' selected' : ''}" data-option-id="${option.id}" ${isSubmitting ? 'disabled' : ''}>
                    <span class="luqta-option-text">${option.option_text}</span>
                    <span class="luqta-option-radio${isSelected ? ' checked' : ''}">
                      ${isSelected ? '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}
                    </span>
                  </button>
                `;
              }).join('')}
            </div>
            ${feedbackMessage ? `
              <div class="luqta-quiz-feedback incorrect">
                ${ICONS.close}
                ${feedbackMessage}
              </div>
            ` : ''}
            ${isSubmitting ? `
              <div class="luqta-quiz-feedback" style="opacity: 0.7;">
                ${this.t.submitting}
              </div>
            ` : ''}
            <button class="luqta-quiz-submit-btn${hasSelection && !isSubmitting ? ' enabled' : ''}" id="luqta-quiz-submit" ${!hasSelection || isSubmitting ? 'disabled' : ''}>
              ${isSubmitting ? this.t.submitting : answerBtnLabel}
            </button>
          </div>
        </div>
      </div>
    `;

    // Remove existing modal
    this.closeModal();
    this.container.insertAdjacentHTML('beforeend', modalHtml);
    this.setupQuizHandlers();
  }

  private setupQuizHandlers(): void {
    if (!this.currentQuizState) return;

    const optionsContainer = document.getElementById('luqta-quiz-options');
    const submitBtn = document.getElementById('luqta-quiz-submit');

    // Close modal when clicking the overlay background
    const overlay = document.getElementById('luqta-modal');
    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.currentQuizState = null;
        this.closeModal();
      }
    });

    // Option selection — visual only, no API call
    optionsContainer?.querySelectorAll('.luqta-quiz-option').forEach(option => {
      option.addEventListener('click', () => {
        if (!this.currentQuizState || this.currentQuizState.isSubmitting) return;

        const optionId = parseInt(option.getAttribute('data-option-id') || '0');
        this.currentQuizState.selectedOptionId = optionId;
        this.currentQuizState.feedbackMessage = null;

        // Re-render to reflect selection
        this.renderQuizQuestion();
      });
    });

    // Submit button — triggers the API call
    submitBtn?.addEventListener('click', async () => {
      if (!this.currentQuizState || this.currentQuizState.isSubmitting) return;
      if (this.currentQuizState.selectedOptionId === null) return;

      const optionId = this.currentQuizState.selectedOptionId;

      // Set loading state
      this.currentQuizState.isSubmitting = true;
      this.currentQuizState.feedbackMessage = null;
      this.renderQuizQuestion();

      try {
        const response = await this.client.quiz.submitAnswer(
          this.currentQuizState.attemptId,
          this.currentQuizState.currentQuestion.id,
          optionId,
          this.currentQuizState.levelId
        );

        if (!this.currentQuizState) return; // cancelled during request

        if (response.data) {
          const { answer, progress, navigation, current_question } = response.data;

          // Update progress from API
          if (progress) {
            this.currentQuizState.progress = progress;
          }

          if (answer.is_correct) {
            // Correct answer
            this.showToast('success', response.message || this.t.correctAnswer);

            if (progress?.is_completed) {
              // Quiz complete → congratulation
              this.currentQuizState.isSubmitting = false;
              this.closeModal();
              await this.handleQuizCompletion();
            } else if (current_question) {
              // Next question from API response
              this.currentQuizState.currentQuestion = current_question;
              this.currentQuizState.isSubmitting = false;
              this.currentQuizState.feedbackMessage = null;
              this.currentQuizState.selectedOptionId = null;
              if (progress) this.currentQuizState.progress = progress;
              this.renderQuizQuestion();
            } else {
              // Fallback: re-render
              this.currentQuizState.isSubmitting = false;
              this.currentQuizState.selectedOptionId = null;
              this.renderQuizQuestion();
            }
          } else {
            // Incorrect answer - deselect and stay on same question
            this.currentQuizState.isSubmitting = false;
            this.currentQuizState.selectedOptionId = null;
            this.currentQuizState.feedbackMessage = response.message || this.t.incorrectAnswer;
            this.renderQuizQuestion();
          }
        }
      } catch (error) {
        if (!this.currentQuizState) return;
        this.currentQuizState.isSubmitting = false;
        this.currentQuizState.selectedOptionId = null;
        this.currentQuizState.feedbackMessage = null;
        this.renderQuizQuestion();
        this.showToast('error', error instanceof Error ? error.message : 'Failed to submit answer');
      }
    });
  }

  private async handleQuizCompletion(): Promise<void> {
    if (!this.currentQuizState) return;

    const { level } = this.currentQuizState;
    this.currentQuizState = null;

    // No need to call quiz.submit() — the server already marks the quiz
    // as completed when progress.is_completed is true from the answer API.
    await this.onLevelCompleted(level);
  }

  private showQuizResultModal(level: Level, result: {
    results: {
      completed_successfully: boolean;
      obtained_marks: number;
      total_marks: number;
      score_percentage: number;
    };
    statistics: {
      correct_answers: number;
      incorrect_answers: number;
      total_questions: number;
    };
    performance: {
      grade: string;
      remarks: string;
    };
    time: {
      time_taken_formatted: string;
    };
  }): void {
    const passed = result.results.completed_successfully;
    const percentage = Math.round(result.results.score_percentage);

    const modalHtml = `
      <div class="luqta-modal-overlay" id="luqta-modal">
        <div class="luqta-modal">
          <div class="luqta-modal-body">
            <div class="luqta-quiz-result">
              <div class="luqta-result-icon ${passed ? 'passed' : 'failed'}">
                ${passed ? ICONS.trophy : ICONS.close}
              </div>
              <h2 class="luqta-result-title">${this.t.quizCompleted}</h2>
              <div class="luqta-result-grade ${passed ? 'passed' : 'failed'}">
                ${result.performance.grade}
              </div>
              <p class="luqta-result-remarks">${result.performance.remarks}</p>
              <div class="luqta-result-stats">
                <div class="luqta-result-stat">
                  <div class="luqta-result-stat-value">${percentage}%</div>
                  <div class="luqta-result-stat-label">${this.t.yourScore}</div>
                </div>
                <div class="luqta-result-stat">
                  <div class="luqta-result-stat-value">${result.statistics.correct_answers}/${result.statistics.total_questions}</div>
                  <div class="luqta-result-stat-label">${this.t.correctAnswer}</div>
                </div>
                <div class="luqta-result-stat">
                  <div class="luqta-result-stat-value">${result.time.time_taken_formatted}</div>
                  <div class="luqta-result-stat-label">${this.t.timeRemaining}</div>
                </div>
              </div>
              <div class="luqta-result-status ${passed ? 'passed' : 'failed'}">
                ${passed ? this.t.passed : this.t.failed}
              </div>
            </div>
          </div>
          <div class="luqta-modal-footer" style="justify-content: center;">
            <button class="luqta-btn luqta-btn-primary" id="luqta-result-continue">
              ${this.t.continue}
            </button>
          </div>
        </div>
      </div>
    `;

    this.container.insertAdjacentHTML('beforeend', modalHtml);

    document.getElementById('luqta-result-continue')?.addEventListener('click', async () => {
      this.closeModal();

      if (passed) {
        // Mark level as completed and show congratulation
        await this.onLevelCompleted(level);
      } else {
        // Just refresh the contest detail
        if (this.state.selectedContest) {
          this.refreshContestDetail();
        }
      }
    });
  }

  private formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private calculateContestProgress(contest: Contest): number {
    if (!contest.levels || contest.levels.length === 0) return 0;

    if (contest.participate_progress?.status === 'completed') return 100;
    if (contest.participate_progress?.status !== 'in_progress') return 0;

    const completedLevels = contest.levels.filter(l => this.isLevelCompleted(l)).length;
    return Math.round((completedLevels / contest.levels.length) * 100);
  }

  private calculateDaysLeft(endDate: string): number {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  private calculateTimeLeft(endDate: string | number): string | null {
    // Accept numeric epoch (ms or s) as well as ISO strings.
    const end = typeof endDate === 'number'
      ? new Date(endDate > 1_000_000_000_000 ? endDate : endDate * 1000)
      : new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();

    if (diff <= 0) {
      return null; // Contest has ended
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    // If more than 1 day, show days
    if (days > 0) {
      return `${days}d ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    // Show HH:MM:SS format
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  private isLevelCompleted(level: Level | LevelWithProgress): boolean {
    const levelWithProgress = level as LevelWithProgress;

    // Check level_progress from API response - it's an ARRAY, not object
    // level_progress: [{ id, created_at, status: "completed" }] or []
    if (Array.isArray(levelWithProgress.level_progress)) {
      // If array is empty, level is NOT completed
      if (levelWithProgress.level_progress.length === 0) {
        return false;
      }
      // If array has items, ONLY return true if status is explicitly 'completed'
      // status can be 'in_progress', 'completed', or 'not_started'
      const progressStatus = levelWithProgress.level_progress[0]?.status;
      if (progressStatus === 'completed') {
        return true;
      }
      // If status is 'in_progress' or 'not_started', return false
      if (progressStatus === 'in_progress' || progressStatus === 'not_started') {
        return false;
      }
    } else if (levelWithProgress.level_progress && typeof levelWithProgress.level_progress === 'object') {
      // Fallback: if it's an object (old format)
      if (levelWithProgress.level_progress.status === 'completed') {
        return true;
      }
      // If status is 'in_progress' or 'not_started', return false
      if (levelWithProgress.level_progress.status === 'in_progress' || levelWithProgress.level_progress.status === 'not_started') {
        return false;
      }
    }

    // Check isCompleted flag (from compete API)
    if (levelWithProgress.isCompleted === true) {
      return true;
    }

    // Check completed_at timestamp (must be a non-empty string)
    if (levelWithProgress.completed_at && typeof levelWithProgress.completed_at === 'string' && levelWithProgress.completed_at.trim() !== '') {
      return true;
    }

    // Fallback to local tracking
    return (level as any)._completed === true;
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Parse a level date value. Accepts ISO strings and numeric epoch timestamps
   * (seconds or milliseconds — `level_timeline` often arrives as a millisecond
   * timestamp). Returns null if the value cannot be parsed.
   */
  private parseLevelDate(raw: string | number): Date | null {
    if (typeof raw === 'number') {
      const ms = raw > 1_000_000_000_000 ? raw : raw * 1000;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d;
    }
    const asNumber = Number(raw);
    if (raw !== '' && !isNaN(asNumber)) {
      const ms = asNumber > 1_000_000_000_000 ? asNumber : asNumber * 1000;
      const d = new Date(ms);
      if (!isNaN(d.getTime())) return d;
    }
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * Show a modal popup used by both level-availability and
   * contest-availability guards.
   */
  private showUnavailableDialog(title: string, message: string): void {
    // Remove any existing instance
    this.container.querySelector('.luqta-unavailable-overlay')?.remove();

    const overlayHtml = `
      <div class="luqta-modal-overlay luqta-unavailable-overlay" data-luqta-close="1">
        <div class="luqta-modal" role="alertdialog" aria-modal="true">
          <div class="luqta-modal-header">
            <h3 class="luqta-modal-title">${title}</h3>
          </div>
          <div class="luqta-modal-body">
            <p>${message}</p>
          </div>
          <div class="luqta-modal-footer">
            <button class="luqta-btn luqta-btn-primary" data-luqta-close="1">${this.t.ok}</button>
          </div>
        </div>
      </div>
    `;

    this.container.insertAdjacentHTML('beforeend', overlayHtml);
    const overlay = this.container.querySelector('.luqta-unavailable-overlay') as HTMLElement | null;
    if (!overlay) return;

    const close = () => overlay.remove();
    overlay.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      // Close on overlay backdrop click or explicit close buttons
      if (target === overlay || target.closest('[data-luqta-close="1"]')) {
        close();
      }
    });
  }

  private showToast(type: 'success' | 'error' | 'info', message: string): void {
    // Remove existing toast
    const existingToast = this.container.querySelector('.luqta-toast-container');
    if (existingToast) existingToast.remove();

    const icon = type === 'success' ? ICONS.success : (type === 'error' ? ICONS.error : ICONS.info);

    const toastHtml = `
      <div class="luqta-toast-container">
        <div class="luqta-toast ${type}">
          <span class="luqta-toast-icon">${icon}</span>
          <span class="luqta-toast-message">${message}</span>
        </div>
      </div>
    `;

    this.container.insertAdjacentHTML('beforeend', toastHtml);

    // Auto-remove after 3 seconds
    setTimeout(() => {
      this.container.querySelector('.luqta-toast-container')?.remove();
    }, 3000);
  }
}
